import { DOCUMENT, NgTemplateOutlet } from "@angular/common";
import {
  EnrichmentListJourney,
  EnrichmentListJourneyStore,
} from "../../../services/state/enrichment-list-journey.store";
import { EMPTY_TABLE_MESSAGE } from "@app/shared/constants/empty-table.constants";
import { A11yModule } from "@angular/cdk/a11y";
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from "@angular/cdk/drag-drop";
import { ConnectedPosition, OverlayModule } from "@angular/cdk/overlay";
import { HttpErrorResponse } from "@angular/common/http";
import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { HasPermissionDirective } from "@app/shared/directives/has-permission.directive";
import { ImportValidationErrorsModal } from "@app/shared/components/import-validation-errors-modal/import-validation-errors-modal";
import { ImportValidationErrorItem } from "@app/shared/components/import-validation-errors-modal/import-validation-errors-modal.types";
import { EnrichmentPriceListResponse } from "@app/shared/models/enrichment-detail.model";
import { OmniarkToastService } from "@app/shared/services/toast.service";
import {
  isCancellableSchedule,
  isTakeDown,
  PublishSchedule,
  PublishScheduleAction,
} from "@app/shared/models/publish-schedule.model";
import {
  BANGKOK_ZONE_LABEL,
  formatScheduledAt,
} from "@app/shared/utils/publish-schedule.util";
import { EnrichmentService } from "@app/shared/services/enrichment.service";
import { OverlayService } from "@app/shared/services/overlay.service";
import { RoleService } from "@app/shared/services/role.service";
import { Permissions } from "@core/models/permissions.generated";
import { AuthService } from "@core/services/auth.service";
import { FeatureFlagService } from "@core/services/feature-flag.service";
import { PermissionService } from "@core/services/permission.service";
import { logger } from "@core/utils/logger";
import {
  DrawerRef,
  EnrTooltipDirective,
  ModalRef,
  OmniarkUiModule,
  SelectOption,
  TableAction,
  TableColumn,
} from "@corp-ais/omniark-ui";
import { APP_ROUTES } from "@shared/constants/app-routes.constants";
import { downloadExcelBlob } from "@shared/excel/excel-cell.utils";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  of,
  Subject,
  Subscription,
  switchMap,
  take,
} from "rxjs";
import { IPagination } from "servers/database/models/response.model";
import { OmniarkDrawerService } from "../../../../../../shared/services/drawer.service";
import { OmniarkModalService } from "../../../../../../shared/services/modal.service";
import { buildProductDetailAppLink } from "@app/shared/utils/adl-deep-link.util";
import { writeScopeKey } from "@app/shared/utils/write-scope.util";
import {
  isExactMyAisChannel,
  isOnlineStoreChannel,
} from "@app/shared/constants/channel.constants";
import { LIFECYCLE_LABEL } from "@app/shared/models/enrichment-list.model";
import {
  PACKAGE_TONE_CLASS,
  PackageStatusView,
} from "@app/shared/models/package-status.model";
import {
  EnrichmentImportReport,
  PackageExportFormat,
  PackageImportReport,
} from "../../../models/enrichment-import-export.model";
import { EnrichmentChannelDirectory } from "../../../services/enrichment-channel-directory.service";
import { ChannelLinkService } from "../../../services/channel-link/channel-link.service";
import { CHANNEL_PARAM } from "../../../services/channel-link/channel-link.util";
import {
  ENRICHMENT_HEADER_TRANSPORT,
  EnrichedSearchProductOfferingParams,
  ProductOfferingService,
} from "../../../services/enrichment-data.service";
import {
  ENRICHMENT_EXPORT_MAX_PO_IDS,
  EnrichmentImportExportService,
  EnrichmentImportHttpResponse,
  extractEnrichmentImportReport,
  extractPackageImportRefusal,
  ONLINE_STORE_X_CHANNEL,
  PackageImportLostError,
} from "../../../services/enrichment-import-export.service";
import {
  packageImportHasFailedRows,
  packageImportProblems,
  packageImportSummary,
} from "./package-import-report";
import { EnrichmentStateStore } from "../../../services/state/enrichment-state.store";
import { EnrichmentMobilePreviewComponent } from "../../edit/enrichment-mobile-preview/enrichment-mobile-preview";
import { PackageFilterDrawer } from "../enrichment-list-filter/enrichment-list-filter";
import {
  createEmptyPackageFilter,
  looksLikeProductCodes,
  normalizeCodeList,
  priceIdSearch,
  splitCodeTokens,
  PackageFilterData,
} from "../enrichment-list-filter/enrichment-list-filter.model";
import { PackageFilterStateService } from "../enrichment-list-filter/enrichment-list-filter.state";
import {
  CategoryAssignApplied,
  EnrichmentCategoryAssign,
} from "./enrichment-category-assign/enrichment-category-assign";
import { EnrichmentChannelGate } from "./enrichment-channel-gate/enrichment-channel-gate";
import {
  EnrichmentTransitionDrawer,
  TransitionConfirmation,
  TransitionRowsRefresh,
} from "./enrichment-transition-drawer/enrichment-transition-drawer";
import {
  outcomeSummary,
  sendTransitions,
  TransitionOutcome,
} from "./transition-requests";
import { SCOPE_CHANGED, SCOPE_CHANGED_REASON, WriteScope } from "./write-scope";
import {
  NO_OPEN_SCHEDULES,
  OpenScheduleIndex,
  OpenScheduleLoader,
  openScheduleOf,
} from "./open-schedules";
import {
  ineligibility,
  LIFECYCLE_ACTION_ORDER,
  LIFECYCLE_ACTIONS,
  LifecycleAction,
  planTransitions,
  plannedPriceCount,
  scheduleDirection,
  whyNoneEligible,
} from "./price-transitions";
import {
  PackageOwner,
  packageOwner,
} from "@app/feature/management/enrichment-mode/online-store-owner";
import { packageEditorLink } from "@app/feature/management/enrichment-mode/package-editor-url";
import { EnrichmentListPreviewController } from "./enrichment-list-preview.controller";
import { buildSortParam } from "./enrichment-list-sort.util";
import { ChannelGroup, ChannelMeta } from "./enrichment-list.channels";
import {
  ALL_COLUMNS,
  DEFAULT_VISIBLE_COLUMNS,
  PREVIEW_TABLE_ACTION,
  ENRICHMENT_TABLE_COLUMNS,
  ENRICHMENT_TABLE_CONFIG,
  isColumnLocked as isColumnLockedFn,
  loadColumnPreferences,
  saveColumnPreferences,
} from "./enrichment-list.columns";
import { toPriceRows, trackByRowKey } from "./enrichment-list.transformer";
import {
  PRICE_STATUS_TAB,
  PRICE_STATUS_TABS,
  PriceStatusTab,
  PriceStatusTabSpec,
  statusTabQuery,
} from "./price-status-tabs";
import {
  ColumnDef,
  EnrichmentPriceRow,
  ListScope,
  SortState,
  CodeSearchScope,
  CodeToken,
  CodeTokenGroup,
  SearchHint,
  SearchScope,
  SearchScopeOption,
} from "./enrichment-list.types";
import { PriceOffsetBadge } from "../../price-offset-badge/price-offset-badge";
import { EnrichmentModeSwitch } from "@app/feature/management/enrichment-mode/enrichment-mode-switch/enrichment-mode-switch";

const SCHEDULE_DIRECTIONS: readonly PublishScheduleAction[] = [
  "publish",
  "unpublish",
];

type SearchNotice = "movedToAll" | "textCleared";

/** What the rows on screen were listed for. */
interface ShownRows {
  channel: string;
  tab: PriceStatusTab;
  term: string | undefined;
}

/** The list request a package count is asked for. `key` drops what the BFF's
 *  count ignores (paging, sort), so turning a page keeps the count. */
interface CountQuery {
  params: EnrichedSearchProductOfferingParams;
  xChannel: string | null;
  key: string;
}

type PackageCount =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; total: number; lowerBound: boolean }
  | { kind: "failed" };

const PACKAGE_COUNT_IDLE: PackageCount = { kind: "idle" };

function countQuery(
  params: EnrichedSearchProductOfferingParams,
  xChannel: string | null,
): CountQuery {
  const {
    page: _page,
    pageSize: _pageSize,
    sortBy: _sortBy,
    sortDir: _sortDir,
    ...scope
  } = params;
  return { params, xChannel, key: JSON.stringify([xChannel, scope]) };
}

const SEARCH_NOTICES: Record<SearchNotice, SearchHint> = {
  movedToAll: {
    kind: "notice",
    text: "Moved to the All tab. A text search can't be combined with a status tab.",
  },
  textCleared: {
    kind: "notice",
    text: "Text search cleared. A status tab can't be combined with a text search.",
  },
};

function sentCodeScope(params: EnrichedSearchProductOfferingParams): boolean {
  return !!(
    params.id ||
    params.priceId ||
    params.productCode ||
    params.productNumber
  );
}

@Component({
  selector: "app-enrichment-list",
  imports: [
    PriceOffsetBadge,
    A11yModule,
    DragDropModule,
    OverlayModule,
    FormsModule,
    OmniarkUiModule,
    ReactiveFormsModule,
    PackageFilterDrawer,
    EnrichmentMobilePreviewComponent,
    EnrichmentChannelGate,
    EnrichmentCategoryAssign,
    EnrichmentTransitionDrawer,
    HasPermissionDirective,
    ImportValidationErrorsModal,
    EnrTooltipDirective,
    NgTemplateOutlet,
    EnrichmentModeSwitch,
  ],
  providers: [EnrichmentListPreviewController],
  templateUrl: "./enrichment-list.html",
  styleUrl: "./enrichment-list.scss",
  host: {
    "(document:keydown)": "onDocumentKeydown($event)",
  },
})
export class EnrichmentList implements OnInit {
  omniarkModal = inject(OmniarkModalService);
  drawerService = inject(OmniarkDrawerService);
  productOfferingService = inject(ProductOfferingService);
  router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  destroyRef = inject(DestroyRef);
  private readonly preview = inject(EnrichmentListPreviewController);
  private readonly enrichmentState = inject(EnrichmentStateStore);
  private readonly featureFlags = inject(FeatureFlagService);

  /** `enableSaleChannelPOP` — the BE reads sale channels off each POP's
   *  `channel` characteristic instead of the offering's channel[]. It gates
   *  only the CHANNELS column; the rows carry each price's channels either
   *  way. */
  protected readonly perPriceSaleChannels = computed(() =>
    this.featureFlags.get("enableSaleChannelPOP"),
  );

  /** `enablePublishSchedule` — publishing a price at a chosen time instead of
   *  now. Off, nothing here is reachable, and not one request goes to the
   *  schedule routes, which the backend does not even mount with its own flag
   *  off. */
  protected readonly publishScheduleEnabled = computed(() =>
    this.featureFlags.get("enablePublishSchedule"),
  );
  private readonly roleService = inject(RoleService);
  private readonly authService = inject(AuthService);
  private readonly channelDirectory = inject(EnrichmentChannelDirectory);
  private readonly channelLinks = inject(ChannelLinkService);
  private readonly filterState = inject(PackageFilterStateService);
  private readonly journey = inject(EnrichmentListJourneyStore);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly document = inject(DOCUMENT);
  private returnScroll: { top: number; left: number } | null = null;
  private hasCachedBaseline = false;
  private inFlightTerm: string | undefined;
  private get listed(): { term: string | undefined } | null {
    return this.loading ? { term: this.inFlightTerm } : this.shownHere;
  }
  private readonly enrichmentImportExport = inject(
    EnrichmentImportExportService,
  );
  private readonly toast = inject(OmniarkToastService);
  private readonly overlayService = inject(OverlayService);
  private readonly permissionService = inject(PermissionService);
  private readonly enrichmentService = inject(EnrichmentService);
  private readonly openScheduleLoader = inject(OpenScheduleLoader);

  /** Exposes the generated permission constants to the template
   *  (`*hasPermission="Permissions.EnrichmentImport"`). */
  readonly Permissions = Permissions;

  private readonly selectedChannelState = signal<string>(
    this.enrichmentState.getChannel(),
  );
  /** Signal-backed so `availableLifecycleActions` follows a channel change;
   *  every read and write keeps the plain-field shape. */
  get selectedChannel(): string {
    return this.selectedChannelState();
  }
  set selectedChannel(value: string) {
    this.selectedChannelState.set(value);
  }
  channelPanelOpen = false;
  channelSearch = "";

  priceStatusTab: PriceStatusTab = "all";
  readonly priceStatusTabs = PRICE_STATUS_TABS;
  private searchNotice: SearchNotice | null = null;
  /** Null until a load lands, so after a failed first load a retry is not
   *  taken for a repeat of rows already on screen. */
  protected shown: ShownRows | null = null;
  /** {@link shown} while its rows are the picked channel's. After a failed
   *  switch the old channel's rows stay on screen, but they count for no tab
   *  of the new one. */
  protected get shownHere(): ShownRows | null {
    return this.shown?.channel === this.selectedChannel ? this.shown : null;
  }

  searchTerm = "";
  private readonly searchInput$ = new Subject<string>();
  private static readonly SEARCH_DEBOUNCE_MS = 700;
  private static readonly MIN_SEARCH_LENGTH = 3;

  /** The in-flight list request. Every load cancels the previous one before
   *  subscribing (unsubscribing an HttpClient observable aborts the XHR), so
   *  a slow, stale response can never land after — and overwrite — the result
   *  of a newer search / page / channel request. */
  private loadRequest: Subscription | null = null;

  /** The request behind the rows on screen, null when nothing is listed. A
   *  failed load keeps the old rows, so it keeps their count scope too. */
  private countQuery: CountQuery | null = null;
  protected packageCount: PackageCount = PACKAGE_COUNT_IDLE;
  private countRequest: Subscription | null = null;

  /** Open schedules of the prices on this page, read after each list
   *  response (the list payload carries none). The status chip and the
   *  cancel actions read it. */
  protected openSchedules: OpenScheduleIndex = NO_OPEN_SCHEDULES;
  private scheduleRequest: Subscription | null = null;

  @ViewChild("filterDrawerTemplate")
  filterDrawerTemplate!: TemplateRef<unknown>;
  @ViewChild("previewDrawerTemplate")
  previewDrawerTemplate!: TemplateRef<unknown>;
  @ViewChild("channelGateModalTemplate")
  channelGateModalTemplate!: TemplateRef<unknown>;
  @ViewChild("categoryAssignDrawerTemplate")
  categoryAssignDrawerTemplate!: TemplateRef<unknown>;
  @ViewChild("transitionDrawerTemplate")
  transitionDrawerTemplate!: TemplateRef<unknown>;
  @ViewChild("importOptionsModalTemplate")
  importOptionsModalTemplate!: TemplateRef<unknown>;
  @ViewChild("importReportModalTemplate")
  importReportModalTemplate!: TemplateRef<unknown>;
  @ViewChild("packageExportOptionsModalTemplate")
  packageExportOptionsModalTemplate!: TemplateRef<unknown>;
  @ViewChild("enrichmentImportFileInput")
  private importFileInput?: ElementRef<HTMLInputElement>;
  @ViewChild("searchInput", { read: ElementRef })
  searchInput!: ElementRef;

  /** The quick-preview drawer, held so "Open in editor" and Escape can close
   *  it imperatively. A DRAWER, not a modal: the whole point of the
   *  preview is to confirm "is this the package I mean?" before opening the
   *  editor, and that comparison needs the list still visible beside it. */
  private previewDrawerRef: DrawerRef<unknown> | null = null;

  /** Open ref + pending row for the "pick a channel before edit" gate. The
   *  gate only fires for the ApplicationOwner case (no nav-header channel
   *  scope) when the in-page pill is also empty — every other role has a
   *  channel synced from the nav header and skips the gate entirely. */
  private channelGateModalRef: ModalRef | null = null;
  protected pendingEditItem: EnrichmentPriceRow | null = null;
  /** Channel keys the backend says {@link pendingEditItem} can be edited
   *  under (GET …/editable-channels) — the same sale-channel rule and
   *  `enableChannelRuleAll` gate the channel views apply. Null while pending
   *  or after a failure: the gate then falls back to the row's salesChannels. */
  protected pendingEditAllowedKeys: readonly string[] | null = null;
  /** True while the editable-channels request for the open gate is in
   *  flight; the gate shows placeholder rows meanwhile. */
  protected pendingEditChannelsLoading = false;

  /** Open ref for the bulk "Assign shelves" drawer so it can be closed
   *  imperatively once the assign resolves. */
  private categoryAssignDrawerRef: DrawerRef<unknown> | null = null;
  protected assignScope: WriteScope | null = null;

  /** The confirm drawer every lifecycle action goes through, and what it was
   *  opened for. */
  private transitionDrawerRef: DrawerRef<unknown> | null = null;
  protected transitionAction: LifecycleAction | null = null;
  protected transitionRows: EnrichmentPriceRow[] = [];
  /** The channel the drawer's rows were listed on, and {@link writeScope}
   *  when it opened. */
  protected transitionChannel = "";
  private transitionScope = "";
  /** True from confirm until every package of the run has an outcome, even
   *  after the drawer that confirmed it has closed. */
  protected transitionSending = false;
  protected transitionOutcomes: readonly TransitionOutcome[] | null = null;

  // ── Bulk import/export (enrichment:import / enrichment:export) ─────────────
  /** File picked from the hidden `<input type="file">`, held until the user
   *  confirms dry-run vs. real import in {@link importOptionsModalTemplate}. */
  pendingImportFile: File | null = null;
  /** Staged in the pre-import modal; read only at confirm time. */
  importDryRun = false;

  /** Dry-run checkbox change. The enr-checkbox `(change)` binding receives
   *  the component's boolean emit AND the native DOM change event bubbling
   *  out of its inner <input> (the lib never stops propagation) — assigning
   *  `$event` directly locked the flag truthy (an Event object). Accept only
   *  the boolean emit. */
  onImportDryRunChange(checked: unknown): void {
    if (typeof checked === "boolean") this.importDryRun = checked;
  }
  private importOptionsModalRef: ModalRef<unknown> | null = null;
  private importReportModalRef: ModalRef<unknown> | null = null;
  readonly importValidationErrors = signal<ImportValidationErrorItem[]>([]);
  importReportSummary = "";
  importReportTruncated = false;
  importReportItemNoun = "validation error";

  /** Online Store imports and exports go through the package endpoints;
   *  every other channel keeps the v1 workbook. */
  get isOnlineStoreScope(): boolean {
    return isOnlineStoreChannel(this.selectedChannel);
  }

  /** Set while an Online Store package import runs in the background. It
   *  can take minutes, so the page stays usable instead of sitting under the
   *  loading overlay; the Import button spins and the text says why. */
  readonly packageImportStatus = signal<string | null>(null);

  /** Staged in the Online Store pre-export modal. */
  packageExportLegacyLayout = false;
  onPackageExportLegacyChange(checked: unknown): void {
    if (typeof checked === "boolean") this.packageExportLegacyLayout = checked;
  }
  private packageExportOptionsModalRef: ModalRef<unknown> | null = null;

  tableData: EnrichmentPriceRow[] = [];
  cachedData: EnrichmentPriceRow[] = [];

  /** Selected rows by `key`, persisted across server-side page changes so the
   *  checkbox state survives navigating to another page and back. */
  private readonly selected = new Map<string, EnrichmentPriceRow>();
  /** `<enr-table>` `[checked]` input — the persisted selection. The table
   *  matches these against the current page through `trackByRowKey`, so
   *  off-page selections are re-checked when their page loads again. */
  checkedRows: EnrichmentPriceRow[] = [];
  readonly trackByRowKey = trackByRowKey;
  cachedPagination: IPagination = {
    page: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  };

  readonly tableColumns: TableColumn<EnrichmentPriceRow>[] =
    ENRICHMENT_TABLE_COLUMNS;
  /** Lifecycle actions this view may offer, in menu order. Hidden rather
   *  than greyed, because they are facts about the whole view: a channel in
   *  scope (the BFF answers 400 without one), the user's permission, and the
   *  schedule flag. */
  readonly availableLifecycleActions = computed<LifecycleAction[]>(() =>
    LIFECYCLE_ACTION_ORDER.filter((action) => {
      if (!this.isChannelSelected) return false;
      if (scheduleDirection(action) && !this.publishScheduleEnabled()) {
        return false;
      }
      return this.permissionService
        .permissions()
        .has(LIFECYCLE_ACTIONS[action].permission);
    }),
  );

  /** Cancelling a pending schedule, either direction, is gated on
   *  `enrichment:publish` by the BFF. */
  private readonly cancelScheduleAvailable = computed(
    () =>
      this.isChannelSelected &&
      this.publishScheduleEnabled() &&
      this.permissionService.permissions().has(Permissions.EnrichmentPublish),
  );

  /** Preview, Edit, the available lifecycle actions, each greyed on a row it
   *  cannot apply to (a row action is a selection of one), then a cancel per
   *  direction, shown only on a row whose price has that schedule open.
   *  A `computed`, so `<enr-table>` gets a new `[actions]` only when the
   *  availability changes; the per-row callbacks read the current page. */
  readonly tableActions = computed<TableAction<EnrichmentPriceRow>[]>(() => [
    PREVIEW_TABLE_ACTION,
    { id: "edit", label: "Edit price", icon: "edit" },
    ...this.availableLifecycleActions().map(
      (action): TableAction<EnrichmentPriceRow> => {
        const spec = LIFECYCLE_ACTIONS[action];
        return {
          id: spec.menuId,
          label: spec.label,
          icon: spec.icon,
          variant: action === "reject" ? "error" : undefined,
          tooltip: spec.tooltip,
          disabled: (row) =>
            this.transitionSending ||
            ineligibility(row, action, this.openSchedules) !== null,
        };
      },
    ),
    ...(this.cancelScheduleAvailable()
      ? SCHEDULE_DIRECTIONS.map(
          (direction): TableAction<EnrichmentPriceRow> => ({
            id: `cancel-schedule-${direction}`,
            label:
              direction === "unpublish"
                ? "Cancel scheduled stop selling"
                : "Cancel scheduled publish",
            icon: "event_repeat",
            tooltip:
              "Cancel this price's pending schedule. One the job has already started cannot be cancelled.",
            visible: (row) =>
              !!openScheduleOf(this.openSchedules, row.priceId, direction),
            disabled: (row) => {
              const open = openScheduleOf(
                this.openSchedules,
                row.priceId,
                direction,
              );
              return !open || !isCancellableSchedule(open);
            },
          }),
        )
      : []),
  ]);

  /** Every column the picker and the table know, minus CHANNELS while
   *  `enableSaleChannelPOP` is off. */
  get allColumns(): ColumnDef[] {
    return this.perPriceSaleChannels()
      ? ALL_COLUMNS
      : ALL_COLUMNS.filter((c) => c.key !== "channels");
  }

  // enr-table's paginator is uncontrolled: it only resyncs its internal page
  // when this `config` input's reference changes (its own ngOnChanges). A new
  // object handed out by snapToFirstPage() is what snaps the displayed page back
  // to 1 on external triggers (e.g. channel switch). Page clicks / size changes
  // are owned by the table itself, so they never go through snapToFirstPage().
  tableConfig: typeof ENRICHMENT_TABLE_CONFIG & { currentPage: number } = {
    ...ENRICHMENT_TABLE_CONFIG,
    currentPage: 1,
  };

  visibleColumns: string[] = [...DEFAULT_VISIBLE_COLUMNS];
  /** Display order of every REORDERABLE (non-locked) column for the picker —
   *  decoupled from visibility so toggling a column off doesn't move it. Built
   *  on load (visible-in-saved-order, then hidden in master order) and only
   *  changed by drag. `visibleColumns` stays synced to this order so the table
   *  ([columns]=orderedTableColumns) reflects it. */
  columnOrder: string[] = [];
  /** Staged picker edits. The popover binds to these; they are committed to
   *  `columnOrder`/`visibleColumns` (and the table) only when the user clicks
   *  Apply. Re-seeded from the committed state every time the popover opens, so
   *  closing without Apply discards the edits. */
  private pendingOrder: string[] = [];
  private pendingVisible = new Set<string>();
  /** Per-key timestamp of the last accepted toggle, used to swallow the
   *  enr-checkbox's duplicate `(change)` echo (it fires twice per click — its
   *  <label> both wraps AND `for`-targets the <input>, double-activating across
   *  event-loop turns). Reset whenever the picker opens, so a deliberate
   *  re-toggle in a later session is never blocked. */
  private readonly lastToggleAt = new Map<string, number>();
  /** Window within which a second `(change)` for the same key is treated as
   *  the checkbox echo (not a deliberate re-toggle). */
  private static readonly CHECKBOX_ECHO_MS = 250;

  sort: SortState = { key: "updated", dir: "desc" };
  columnsPanelOpen = false;
  scopePanelOpen = false;

  /** Draft search scope, restored together with the committed filters on return. */
  private pickedScope: SearchScope = "all";

  /** The text in the search box while a CODE scope is active. Kept apart from
   *  `searchTerm` (which is the free-text `q`) because the two are mutually
   *  exclusive on the wire: mixing them into one field would make "is a
   *  search active?" unanswerable. Uncommitted — Enter promotes it into
   *  `appliedFilters`; exact-match scopes must not fire per keystroke. */
  codeDraft = "";

  /** One row per scope the single toolbar box can be pointed at. `short` is
   *  what the pill shows (it has ~130px), `label` what the popover lists. */
  readonly searchScopeOptions: readonly SearchScopeOption[] = [
    {
      value: "all",
      short: "All fields",
      label: "All fields",
      placeholder:
        "Search by price or product offering name, ID, or description",
      hint: "",
      chips: [],
      detail: "Price or product offering name, ID or description",
    },
    {
      value: "offeringCode",
      short: "Offering ID",
      label: "Offering ID",
      placeholder: "e.g. V1StGXRZ5j3, a1b2c3d4e5f",
      hint: "Exact match · paste a list (commas, spaces or new lines)",
      chips: [],
      detail: "Exact offering IDs — paste a list, one or many",
    },
    {
      value: "priceId",
      short: "Price ID",
      label: "Price ID",
      placeholder: "e.g. enrpop260600060, enrpop260600061",
      hint: "Exact match · paste a list · shows the product offering each price belongs to",
      chips: [],
      detail:
        "Exact price IDs — paste a list; shows the product offering each price belongs to",
    },
    {
      value: "productRefs",
      short: "Product code",
      label: "Product code / number",
      placeholder: "e.g. O210409863168, P100012345",
      hint: "Exact match · paste a list",
      chips: [
        { key: "O…", text: "product code" },
        { key: "P…", text: "product number" },
      ],
      detail: "Exact codes — O… is a product code, P… a product number",
    },
  ];

  /** Backed by {@link PackageFilterStateService} so the (re-created-on-open)
   *  filter drawer can seed its form from the applied filters. */
  get appliedFilters(): PackageFilterData | null {
    return this.filterState.applied();
  }
  set appliedFilters(value: PackageFilterData | null) {
    this.filterState.applied.set(value);
  }

  /** True while the "Deleted packages" filter (`deleted=only`) is applied.
   *  Every row is then a soft-deleted price, so the template shows the
   *  deleted-view banner and the Deleted pill in place of the lifecycle pill. */
  get isDeletedView(): boolean {
    return this.appliedFilters?.deleted === "only";
  }

  /** Sort dropdown — wired to the central `<enr-select>` component. */
  /** The BFF sorts packages only; within one package it returns the prices
   *  in price-id order, so every option names the package field it sorts. */
  readonly sortOptions: SelectOption[] = [
    { value: "updated:desc", label: "Sort · Offering updated, newest" },
    { value: "updated:asc", label: "Sort · Offering updated, oldest" },
    { value: "version:desc", label: "Sort · Offering version, newest" },
    { value: "version:asc", label: "Sort · Offering version, oldest" },
    { value: "product:asc", label: "Sort · Offering name (A→Z)" },
    { value: "product:desc", label: "Sort · Offering name (Z→A)" },
    { value: "offeringCode:asc", label: "Sort · Offering ID (A→Z)" },
    { value: "offeringCode:desc", label: "Sort · Offering ID (Z→A)" },
  ];
  readonly sortControl = new FormControl<string>("updated:desc", {
    nonNullable: true,
  });

  config = {
    pageable: true,
    pageSize: 10,
    pageSizeOptions: [10, 50],
    filterable: true,
    selectable: true,
    emptyMessage: EMPTY_TABLE_MESSAGE,
    emptyIcon: "search_off",
    serverSide: true,
    currentPage: 1,
    totalItems: 0,
  };
  loading = false;

  searchTruncated = false;

  /** The free-text term of a search that found nothing and reads as product
   *  codes, offered for a one-click search in the Product code scope. `q`
   *  never matches a product code, so the user would otherwise conclude the
   *  price does not exist. Null unless the last load was that search. */
  codeSearchOffer: string | null = null;

  allStatusesOffer = false;

  /** Channel popover: align under the pill's left edge; flip above if no room. */
  readonly channelPopoverPositions: ConnectedPosition[] = [
    {
      originX: "start",
      originY: "bottom",
      overlayX: "start",
      overlayY: "top",
      offsetY: 8,
    },
    {
      originX: "start",
      originY: "top",
      overlayX: "start",
      overlayY: "bottom",
      offsetY: -8,
    },
  ];

  /** Columns panel: right-aligned under the grid button (it sits at the far
   *  right of the toolbar), flipping above when there's no room below. */
  readonly columnsPanelPositions: ConnectedPosition[] = [
    {
      originX: "end",
      originY: "bottom",
      overlayX: "end",
      overlayY: "top",
      offsetY: 6,
    },
    {
      originX: "end",
      originY: "top",
      overlayX: "end",
      overlayY: "bottom",
      offsetY: -6,
    },
  ];

  // ── Preview rail (delegated) ───────────────────────────────────────────────
  get previewItem(): EnrichmentPriceRow | null {
    return this.preview.previewItem;
  }
  get previewLoading(): boolean {
    return this.preview.previewLoading;
  }
  get previewError(): string | null {
    return this.preview.previewError;
  }
  /** The tier the preview shows; the list owns it, so the preview opens on
   *  the clicked price and its own tier tabs still switch. */
  get previewPriceIndex(): number | null {
    return this.preview.priceIndex();
  }
  onPreviewPriceIndexChange(index: number): void {
    this.preview.priceIndex.set(index);
  }
  /** Offer classification for the preview meta, resolved from the fetched
   *  detail the way the editor resolves it. */
  get previewOfferClassification(): string {
    return this.preview.previewOfferClassification;
  }

  // ── Derived getters ────────────────────────────────────────────────────────
  /** Column defs handed to `<enr-table [columns]>` — EVERY column in display
   *  order (locked first, then the drag-ordered reorderable list). Hiding is
   *  delegated to `[visibleColumns]`, which the enr-table applies as a
   *  membership filter (same as the online-store dashboard, where hide works).
   *
   *  Why all columns, not just the visible ones: feeding the visible subset
   *  shrank `[columns]` on every hide, and the table reflected reorder but NOT
   *  the removal — a freshly-unchecked column kept rendering. Keeping the
   *  `[columns]` membership stable (only the order changes, on drag) and hiding
   *  via `[visibleColumns]` makes both work. */
  get orderedTableColumns(): TableColumn<EnrichmentPriceRow>[] {
    const offered = new Set(this.allColumns.map((c) => c.key));
    const byKey = new Map(
      this.tableColumns
        .filter((c) => offered.has(c.key))
        .map((c) => [
          c.key,
          c.key === "status" ? { ...c, label: this.statusColumnLabel } : c,
        ]),
    );
    const lockedKeys = this.allColumns
      .filter((c) => this.isColumnLocked(c.key))
      .map((c) => c.key);
    return [...lockedKeys, ...this.columnOrder]
      .map((key) => byKey.get(key))
      .filter((c): c is TableColumn<EnrichmentPriceRow> => !!c);
  }

  /** The STATUS header names whose status the column shows: the channel's,
   *  or the catalog's when no channel is in scope. */
  get statusColumnLabel(): string {
    return this.isChannelSelected
      ? `STATUS · ${this.currentChannelLabel}`
      : "STATUS";
  }

  /** Non-locked columns for the picker's drag list, in the PENDING (staged)
   *  order — the popover reflects edits live while the table stays put until
   *  Apply. Locked columns are rendered separately and never appear here. */
  get reorderableColumns(): ColumnDef[] {
    const byKey = new Map(this.allColumns.map((c) => [c.key, c]));
    return this.pendingOrder
      .map((key) => byKey.get(key))
      .filter((c): c is ColumnDef => !!c && !this.isColumnLocked(c.key));
  }

  /** Reorderable column keys in their natural display order: visible ones in
   *  the given order, then hidden ones in master order. Seeds `columnOrder` on
   *  load and the pending order on reset. */
  private buildColumnOrder(visible: string[] = this.visibleColumns): string[] {
    const reorderable = (key: string): boolean => !this.isColumnLocked(key);
    const visibleReorderable = visible.filter(reorderable);
    const hidden = this.allColumns
      .map((c) => c.key)
      .filter((key) => reorderable(key) && !visible.includes(key));
    return [...visibleReorderable, ...hidden];
  }

  /** Rebuild `visibleColumns` from the current visibility set, ordered to match
   *  `columnOrder` (locked-visible columns pinned first). Keeps the table order
   *  in step with the picker's drag order without coupling visibility to it. */
  private orderVisible(visibleSet: Set<string>): string[] {
    const lockedVisible = this.allColumns
      .filter((c) => this.isColumnLocked(c.key) && visibleSet.has(c.key))
      .map((c) => c.key);
    const orderedVisible = this.columnOrder.filter((key) =>
      visibleSet.has(key),
    );
    return [...lockedVisible, ...orderedVisible];
  }

  get totalPages(): number {
    const total = this.config.totalItems;
    if (!total) return 1;
    return Math.max(1, Math.ceil(total / this.config.pageSize));
  }

  get sortValue(): string {
    return `${this.sort.key}:${this.sort.dir}`;
  }

  get hasAnyFilter(): boolean {
    return !!this.searchTerm || this.drawerFilterCount > 0;
  }

  get activeFilterCount(): number {
    return (this.searchTerm ? 1 : 0) + this.drawerFilterCount;
  }

  private get isBaselineView(): boolean {
    return this.drawerFilterCount === 0 && this.priceStatusTab === "all";
  }

  get drawerFilterCount(): number {
    if (!this.appliedFilters) return 0;
    return Object.values(this.appliedFilters).filter(
      (v) => typeof v === "string" && v.trim() !== "",
    ).length;
  }

  private static readonly LIST_TOTAL_CAP = 10_000;
  protected readonly statusLookupCap = (5_000).toLocaleString();

  get totalCount(): string {
    const total = this.config.totalItems;
    const floor =
      this.searchTruncated || total >= EnrichmentList.LIST_TOTAL_CAP;
    return `${total.toLocaleString()}${floor ? "+" : ""}`;
  }

  get totalLabel(): string {
    return `${this.totalCount} ${this.totalCount === "1" ? "price" : "prices"}`;
  }

  protected get canCountPackages(): boolean {
    return (
      this.countQuery !== null && this.shownHere?.tab === this.priceStatusTab
    );
  }

  protected get packageTotal(): string {
    const count = this.packageCount;
    if (count.kind !== "ready") return "";
    return `${count.total.toLocaleString()}${count.lowerBound ? "+" : ""}`;
  }

  protected get packageCountTooltip(): string {
    const base =
      "Product offerings the listed prices belong to. A price shared by two product offerings is two rows but counts each product offering once, so the two numbers are not a ratio.";
    return this.packageCount.kind === "ready" && this.packageCount.lowerBound
      ? `${base} At least this many; the search hit its cap.`
      : base;
  }

  countPackages(): void {
    const query = this.countQuery;
    if (!query || this.loading) return;
    this.countRequest?.unsubscribe();
    this.packageCount = { kind: "loading" };
    this.countRequest = this.productOfferingService
      .getEnrichmentCount(query.params, query.xChannel)
      .subscribe({
        next: ({ total, isExact, searchTruncated }) => {
          this.packageCount = {
            kind: "ready",
            total,
            lowerBound: !isExact || searchTruncated,
          };
        },
        error: (error) => {
          logger.error("Error counting packages:", error);
          this.packageCount = { kind: "failed" };
        },
      });
  }

  /** Unsubscribing aborts a count still in flight, so an answer for the old
   *  scope never lands. */
  private resetPackageCount(): void {
    this.countRequest?.unsubscribe();
    this.countRequest = null;
    this.packageCount = PACKAGE_COUNT_IDLE;
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  ngOnInit(): void {
    const saved = this.journey.take(this.journeyContext());
    const seededQuery = this.route.snapshot.queryParamMap.get("q");
    const returning =
      !seededQuery || seededQuery === saved?.entryQuery ? saved : null;
    this.appliedFilters = null;
    this.visibleColumns = loadColumnPreferences();
    this.columnOrder = this.buildColumnOrder();
    this.seedPendingColumns();
    // One-shot fetch of the channel master (GET /api/v1/channel) — the
    // directory caches in-memory so the gate modal reuses the same payload.
    this.channelDirectory.prime();
    // Non-admin users don't get the in-page channel pill — their x-channel is
    // driven by the active role channel on the nav header. Mirror it into the
    // enrichment state store (where `ProductOfferingService.channelHeader()`
    // reads) and keep it in sync as the nav header changes.
    this.syncRoleChannelIntoStore(false);
    if (returning) this.restoreJourney(returning);
    // syncRoleChannelIntoStore's no-op branch (channel already matches the
    // store, e.g. a bound role with no channel assigned) can skip
    // resetForScopeChange() entirely — patch `selectable` here too so the
    // checkbox column reflects the bulk permissions from the first render
    // regardless of which branch ran above.
    this.tableConfig = {
      ...this.tableConfig,
      selectable: this.canSelectRowsForBulkActions,
      currentPage: this.config.currentPage,
      pageSize: this.config.pageSize,
    };
    this.roleService.channelChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncRoleChannelIntoStore());
    // Channel is hydrated from localStorage by EnrichmentStateStore itself,
    // so we read straight from the store rather than re-pushing here.
    this.destroyRef.onDestroy(() => this.cancelInFlightLoad());
    this.searchInput$
      .pipe(
        debounceTime(EnrichmentList.SEARCH_DEBOUNCE_MS),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((term) => {
        const sendable = this.sendableTerm(term);
        const repeatsListed =
          sendable === this.listed?.term && this.priceStatusTab === "all";
        if (!sendable || repeatsListed) return;
        this.config = { ...this.config, currentPage: 1 };
        this.clearSearchExclusiveFilters();
        this.loadPrices(sendable, 1);
      });

    // Explicit deep links take precedence over a pending return journey.
    if (seededQuery && !returning) {
      this.resetSearchScope();
      this.searchTerm = seededQuery;
    }

    // Wire <enr-select> sort control → existing sort apply pipeline.
    this.sortControl.setValue(this.sortValue, { emitEvent: false });
    this.sortControl.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (!value) return;
        const [key, dir] = value.split(":") as [string, "asc" | "desc"];
        this.sort = { key, dir };
        this.applySortChange();
      });

    this.loadPrices(this.searchTerm || undefined, this.config.currentPage);
    this.openLinkedChannel();
  }

  /** A link's `?channel=` picks the channel for a role on all channels,
   *  read by the same resolver as the editor's link guard. A bound role
   *  stays on its own channel; a channel that does not resolve, or that
   *  another role covers, leaves the list as it opened. */
  private openLinkedChannel(): void {
    if (!this.isApplicationOwnerChannel) return;
    const value = this.route.snapshot.queryParamMap.get(CHANNEL_PARAM);
    if (!value) return;
    this.channelLinks
      .ready()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const outcome = this.channelLinks.resolve(value);
        if (outcome.kind === "ok" && outcome.anyChannel) {
          this.selectChannel(outcome.channel.key);
        }
      });
  }

  @HostListener("document:click")
  onDocumentClick(): void {
    if (this.columnsPanelOpen) this.columnsPanelOpen = false;
    if (this.channelPanelOpen) this.channelPanelOpen = false;
    if (this.scopePanelOpen) this.scopePanelOpen = false;
  }

  @HostListener("document:keydown.escape")
  onEscape(): void {
    if (this.scopePanelOpen) {
      this.scopePanelOpen = false;
      return;
    }
    if (this.channelPanelOpen) {
      this.channelPanelOpen = false;
      return;
    }
    if (this.previewItem) this.closePreview();
  }

  onDocumentKeydown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
      event.preventDefault();
      this.focusSearchInput();
    }
  }

  // ── Search scope ───────────────────────────────────────────────────────────
  /** The code fields currently committed on `appliedFilters`, in popover
   *  order. Normally 0 or 1 — the toolbar only ever commits one — but the
   *  DRAWER can set all three at once, which is why this is a list. */
  get committedCodeScopes(): CodeSearchScope[] {
    const f = this.appliedFilters;
    if (!f) return [];
    const scopes: CodeSearchScope[] = [];
    if (f.offeringCode?.trim()) scopes.push("offeringCode");
    if (f.priceId?.trim()) scopes.push("priceId");
    if (f.productRefs?.trim()) scopes.push("productRefs");
    return scopes;
  }

  /** True when the drawer has more than one code field set at once. The box
   *  can only honestly show ONE scope, so rather than pick a winner and lie
   *  about the other, it goes read-only and names the count — the drawer (or
   *  the popover's "Clear code search") is where a multi-code state is
   *  edited. The backend ANDs these `$in`s, so the state is degenerate
   *  anyway; an honest read-only box beats clever routing. */
  get isMultiCodeScope(): boolean {
    return this.committedCodeScopes.length > 1;
  }

  /** Single source of truth for which scope the box is in.
   *
   *  Derived from `appliedFilters` whenever exactly one code is committed, so
   *  a code applied from the DRAWER retargets the toolbar box automatically —
   *  no second copy of the scope to keep in sync, and no state where the box
   *  says "All fields" while the table is code-filtered. `pickedScope` only
   *  answers for the uncommitted case. */
  get searchScope(): SearchScope {
    const committed = this.committedCodeScopes;
    return committed.length === 1 ? committed[0] : this.pickedScope;
  }

  /** A code scope the user can actually type into (i.e. not the read-only
   *  multi-code state). The routing predicate for every search entry point. */
  get isCodeScopeActive(): boolean {
    return this.searchScope !== "all" && !this.isMultiCodeScope;
  }

  get activeScopeOption(): SearchScopeOption {
    return (
      this.searchScopeOptions.find((o) => o.value === this.searchScope) ??
      this.searchScopeOptions[0]
    );
  }

  /** Pill text. The multi-code state names the count instead of a scope. */
  get searchScopeLabel(): string {
    if (this.isMultiCodeScope) {
      return `Codes · ${this.committedCodeCount}`;
    }
    const count = this.committedCodeCount;
    return count > 1
      ? `${this.activeScopeOption.short} · ${count}`
      : this.activeScopeOption.short;
  }

  // ── Committed-code chips ───────────────────────────────────────────────────
  /** The committed codes, one group per committed field, derived straight
   *  from `appliedFilters` (no second copy). The chip strip under the box
   *  renders these — a pasted list of twelve codes is otherwise a single
   *  scrolled-off string with no way to see what was counted, which was
   *  dropped, or to take one out without retyping the rest. */
  get committedCodeGroups(): CodeTokenGroup[] {
    const f = this.appliedFilters;
    if (!f) return [];
    return this.committedCodeScopes.map((scope) => ({
      scope,
      label:
        this.searchScopeOptions.find((o) => o.value === scope)?.short ?? scope,
      tokens: splitCodeTokens(f[scope]).map((value): CodeToken => ({
        value,
        ignored: scope === "productRefs" && !/^[op]/i.test(value),
      })),
    }));
  }

  /** Total committed codes across every field — what the pill counts. */
  get committedCodeCount(): number {
    return this.committedCodeGroups.reduce((n, g) => n + g.tokens.length, 0);
  }

  /** The × on one chip. Rewrites that one field without the token and
   *  refetches — a real query change, same as `clearCodeScope`, never the
   *  cached baseline. Emptying the last token of a field shrinks
   *  `committedCodeScopes` on its own, so the multi-code state unwinds to a
   *  single scope, and a single scope to none, with no extra bookkeeping. */
  removeCodeToken(scope: CodeSearchScope, value: string): void {
    const f = this.appliedFilters;
    if (!f) return;
    const remaining = splitCodeTokens(f[scope]).filter((t) => t !== value);
    const next = remaining.join(", ");
    this.appliedFilters = { ...f, [scope]: next };
    // Keep the box in step. Once ONE field is left, `searchScope` derives
    // to it (the multi-code state has unwound) and the box must show that
    // field — which may not be the one just edited. Nothing left → stay on
    // the scope the user chose rather than snapping back to "All fields";
    // matches an empty Enter commit.
    const committed = this.committedCodeScopes;
    if (committed.length === 1) {
      this.pickedScope = committed[0];
      this.codeDraft = normalizeCodeList(this.appliedFilters?.[committed[0]]);
    } else if (committed.length === 0) {
      this.pickedScope = scope;
      this.codeDraft = "";
    }
    this.config = { ...this.config, currentPage: 1 };
    this.loadPrices(undefined, 1);
  }

  /** Paste into a code scope. A single-line `<input>` strips CR/LF from
   *  pasted text (HTML value sanitization), so a column of codes copied from
   *  a spreadsheet lands FUSED — "O1\nO2" becomes "O1O2" — and the exact
   *  match then finds nothing. Take over only when the clipboard is a list
   *  (a lone code pastes natively) and merge it into the draft as a comma
   *  list; Enter still commits, a paste never queries by itself. Free text
   *  is untouched: substring search wants the raw string. */
  onSearchPaste(event: ClipboardEvent): void {
    if (!this.isCodeScopeActive) return;
    const text = event.clipboardData?.getData("text/plain") ?? "";
    const pasted = splitCodeTokens(text);
    if (pasted.length < 2 && !/[\r\n\t]/.test(text)) return;
    event.preventDefault();
    this.codeDraft = normalizeCodeList(
      [...splitCodeTokens(this.codeDraft), ...pasted].join(","),
    );
  }

  /** What `<enr-search>` displays. `[value]` is a plain input on the omniark
   *  component (no setter, no ngOnChanges), so a changed binding does reach
   *  it — the getter is what makes switching scope reset the visible text. */
  get searchBoxValue(): string {
    if (this.isMultiCodeScope) {
      const f = this.appliedFilters;
      return this.committedCodeScopes
        .map((scope) => f?.[scope]?.trim() ?? "")
        .join(" · ");
    }
    return this.searchScope === "all" ? this.searchTerm : this.codeDraft;
  }

  get searchBoxPlaceholder(): string {
    return this.activeScopeOption.placeholder;
  }

  get searchBoxDisabled(): boolean {
    return this.isMultiCodeScope;
  }

  /** The line under the box. Rendered by the template rather than fed to
   *  `<enr-search supportingText>` because that input takes a plain string,
   *  and the prefix legend (`O…` / `P…`) only reads well as chips — a
   *  run-on sentence is exactly what it replaced. */
  get searchHint(): SearchHint {
    if (this.isMultiCodeScope) {
      return {
        kind: "warn",
        text: "Several code filters are set — edit them in Filters.",
      };
    }
    if (this.searchScope === "all") {
      if (this.searchNotice) return SEARCH_NOTICES[this.searchNotice];
      // A price id typed into the free-text box is looked up as one
      // (`buildFilterRequest`): say so, or a half-typed id finding nothing
      // reads as a broken search.
      return priceIdSearch(this.searchTerm)
        ? { kind: "notice", text: "Price ID · exact match" }
        : { kind: "none", text: "" };
    }
    return { kind: "scope", text: this.activeScopeOption.hint };
  }

  toggleScopePanel(event: Event): void {
    event.stopPropagation();
    this.scopePanelOpen = !this.scopePanelOpen;
    if (this.scopePanelOpen) this.columnsPanelOpen = false;
  }

  /** Retarget the box. Whatever the previous scope had committed is dropped:
   *  the box shows one scope at a time, and a filter it no longer displays
   *  must not keep filtering the table behind the user's back. */
  selectSearchScope(scope: SearchScope, event: Event): void {
    event.stopPropagation();
    this.scopePanelOpen = false;
    // Re-clicking the option you're already on is a dismiss, not a change —
    // wiping a draft the user is still typing would be a nasty little slip.
    if (scope === this.searchScope && !this.isMultiCodeScope) return;
    const hadCode = this.committedCodeScopes.length > 0;
    this.pickedScope = scope;
    this.codeDraft = "";
    this.searchNotice = null;
    if (hadCode) {
      this.clearCommittedCodes();
      this.config = { ...this.config, currentPage: 1 };
      this.loadPrices(undefined, 1);
    } else if (scope !== "all" && this.searchTerm) {
      // The other direction of the same exclusion: leaving free text behind
      // in a box that no longer sends it would leave the funnel badge
      // claiming a search that isn't reaching the wire.
      this.onClearSearch();
    }
    this.focusSearchInput();
  }

  commitCodeSearch(): void {
    if (!this.isCodeScopeActive) return;
    const scope = this.searchScope;
    // Stored normalized (", "-joined tokens) so the drawer textarea, the box
    // and the chips all show one spelling of the list.
    const value = normalizeCodeList(this.codeDraft);
    const next: PackageFilterData = {
      ...(this.appliedFilters ?? createEmptyPackageFilter()),
      offeringCode: "",
      priceId: "",
      productRefs: "",
    };
    if (scope === "offeringCode") next.offeringCode = value;
    else if (scope === "priceId") next.priceId = value;
    else if (scope === "productRefs") next.productRefs = value;

    // An empty commit is a clear; keep `pickedScope` so the box stays on the
    // scope the user chose rather than snapping back to "All fields".
    this.pickedScope = scope;
    this.appliedFilters = next;
    this.dropSearchTerm();
    this.config = { ...this.config, currentPage: 1 };
    this.loadPrices(undefined, 1);
  }

  /** The × in a code scope. NOT `onClearSearch` — that restores the cached
   *  UNFILTERED baseline without a request, which would show unfiltered rows
   *  while `appliedFilters` still held the code and the funnel still counted
   *  it. Clearing a code is a real query change, so it refetches. */
  clearCodeScope(): void {
    this.codeDraft = "";
    if (this.committedCodeScopes.length === 0) return;
    this.clearCommittedCodes();
    this.config = { ...this.config, currentPage: 1 };
    this.loadPrices(undefined, 1);
  }

  private resetSearchScope(): void {
    this.pickedScope = "all";
    this.codeDraft = "";
    this.clearSearchExclusiveFilters();
  }

  private clearCommittedCodes(): void {
    const f = this.appliedFilters;
    if (!f) return;
    this.appliedFilters = {
      ...f,
      offeringCode: "",
      priceId: "",
      productRefs: "",
    };
  }

  // ── Search ─────────────────────────────────────────────────────────────────
  onSearch(value: string): void {
    // A code scope buffers only. These are exact-match `$in` filters, so
    // debouncing a query per keystroke would fire a request for every prefix
    // of an id and match nothing until the last character — Enter commits.
    // (`<enr-search>` emits `search` on Enter too, so this lands before
    // `keyup.enter` reaches `onSearchEnter`, and the draft is current.)
    if (this.isCodeScopeActive) {
      this.codeDraft = value ?? "";
      return;
    }
    this.searchTerm = value ?? "";
    if (this.searchNotice === "textCleared") this.searchNotice = null;
    if (!this.searchTerm.trim()) {
      this.onClearSearch();
      return;
    }
    this.searchInput$.next(this.searchTerm);
  }

  /** The `<enr-search>` × — routed by scope. `onClearSearch` stays the
   *  free-text-only path because it is also called to drop `q` when switching
   *  INTO a code scope; making it scope-aware would make that ordering
   *  fragile. */
  onSearchClear(): void {
    if (this.searchScope !== "all") {
      this.clearCodeScope();
      return;
    }
    this.onClearSearch();
  }

  onClearSearch(): void {
    if (this.listed && this.listed.term === undefined) {
      this.dropSearchTerm();
      return;
    }
    // Abort any in-flight search so its late response can't clobber the
    // cached baseline we're about to restore — and release the spinner the
    // aborted request would otherwise leave stuck on.
    this.cancelInFlightLoad();
    this.loading = false;
    this.dropSearchTerm();
    if (!this.hasCachedBaseline || !this.isBaselineView) {
      this.snapToFirstPage();
      this.loadPrices(undefined, 1);
      return;
    }
    this.tableConfig = {
      ...this.tableConfig,
      emptyMessage: EMPTY_TABLE_MESSAGE,
      currentPage: this.cachedPagination.page,
      pageSize: this.cachedPagination.pageSize,
    };
    this.tableData = this.cachedData;
    this.shown = { channel: this.selectedChannel, tab: "all", term: undefined };
    this.loadPageSchedules();
    // Reverting to the cached unfiltered baseline (no new request fires, so
    // no fresh header). That baseline carries no `q`, hence no POP pre-query
    // and never truncates — clear the banner here too.
    this.searchTruncated = false;
    this.codeSearchOffer = null;
    this.allStatusesOffer = false;
    this.refreshCheckedRows();
    this.config = {
      ...this.config,
      currentPage: this.cachedPagination.page,
      pageSize: this.cachedPagination.pageSize,
      totalItems: this.cachedPagination.totalItems,
    };
  }

  private dropSearchTerm(): void {
    this.searchTerm = "";
    this.searchNotice = null;
    this.searchInput$.next("");
  }

  onSearchEnter(): void {
    // A caller that sets `searchTerm` directly (rather than typing into the
    // box) can leave free text pending while a code scope is committed — a
    // state the toolbar itself cannot produce, since the box shows one scope
    // at a time. The free text wins: swallowing it would drop a search the
    // caller asked for.
    // `searchTerm` is the tell: every toolbar route INTO a code scope blanks
    // it (scope switch, commit, drawer apply), so a non-empty one here can
    // only have been assigned directly.
    if (this.isCodeScopeActive && this.searchTerm.trim()) {
      this.resetSearchScope();
    }
    if (this.isCodeScopeActive) {
      // No MIN_SEARCH_LENGTH gate here: that guard protects the free-text
      // substring scan and its POP pre-query. An exact `$in` is cheap, and
      // offering ids are legitimately short.
      this.commitCodeSearch();
      return;
    }
    const searchTerm = this.normalizeSearchTerm(this.searchTerm);
    if (!searchTerm) {
      this.onClearSearch();
      return;
    }

    if (this.isSearchTermTooShort(searchTerm)) return;

    this.clearSearchExclusiveFilters();
    this.config = { ...this.config, currentPage: 1 };
    this.loadPrices(searchTerm, 1);
  }

  onClearAllFilters(): void {
    this.appliedFilters = null;
    this.pickedScope = "all";
    this.codeDraft = "";
    this.onClearSearch();
    this.snapToFirstPage();
    this.loadPrices(undefined, 1);
  }

  // ── Pagination ─────────────────────────────────────────────────────────────
  /** Snap the table paginator back to page 1. External triggers (channel switch)
   *  reset our `config.currentPage`; pass a fresh `tableConfig` object so
   *  enr-table picks up the new page. Not used by the table's own page/size
   *  events, so it can't fight the user. */
  private snapToFirstPage(): void {
    this.config = { ...this.config, currentPage: 1 };
    this.tableConfig = {
      ...ENRICHMENT_TABLE_CONFIG,
      currentPage: 1,
      pageSize: this.config.pageSize ?? ENRICHMENT_TABLE_CONFIG.pageSize,
      // Checkbox column whenever a bulk action is reachable — see
      // canSelectRowsForBulkActions's doc comment.
      selectable: this.canSelectRowsForBulkActions,
    };
  }

  private resetForScopeChange(): void {
    this.snapToFirstPage();
    this.hasCachedBaseline = false;
    // Every caller (channel switch / clear, role channel sync) changes the
    // table scope, which invalidates the current selection — clear it.
    //
    // Product decision (explicit, superseding an interim fix): the ticked
    // rows belong to the PREVIOUS view's context, so a channel change —
    // none→channel, channel→channel, or channel→none — always clears the
    // export selection, even though the checkbox column itself may stay
    // visible throughout (the bulk permissions are channel-independent —
    // see canSelectRowsForBulkActions). The correct flow is: pick a channel
    // FIRST, then tick rows, then export — the hover tooltip on the
    // (disabled-until-selected) Export button already guides that. A prior
    // revision made this conditional (only clear when the column actually
    // disappears) to fix a different complaint (selection silently vanishing
    // on a channel pick); that was an over-correction the user explicitly
    // reversed.
    this.clearSelection();
    // The confirm drawer was planned on the old scope. A run it started goes
    // on and reports through a toast.
    this.closeTransition();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    if (page === this.config.currentPage) return;
    this.loadPrices(this.searchTerm || undefined, page);
  }

  onPageSizeSelect(event: Event): void {
    const size = Number((event.target as HTMLSelectElement).value);
    this.config = { ...this.config, pageSize: size, currentPage: 1 };
    this.loadPrices(this.searchTerm || undefined, 1);
  }

  /** `<enr-table>` page change event. */
  onPageChange(page: number | { page: number }): void {
    const next = typeof page === "number" ? page : page.page;
    if (typeof next !== "number") return;
    this.goToPage(next);
  }

  /** `<enr-table>` page size change event. */
  onPageSizeChange(size: number | { pageSize: number }): void {
    const next = typeof size === "number" ? size : size.pageSize;
    if (typeof next !== "number") return;
    this.config = { ...this.config, pageSize: next, currentPage: 1 };
    this.loadPrices(this.searchTerm || undefined, 1);
  }

  /** `<enr-table>` selection change — reconcile the persistent selection with
   *  the current page's checkboxes. `selectionChange` only reports the visible
   *  page, so drop every key on this page first, then re-add the checked ones.
   *  Off-page selections stay in the map and are restored when their page
   *  loads again (see `refreshCheckedRows`). */
  onSelectionChange(selected: EnrichmentPriceRow[]): void {
    for (const row of this.tableData) this.selected.delete(row.key);
    for (const row of selected) this.selected.set(row.key, row);

    // Client-side enforcement of the backend's export cap (spec §3.4:
    // poIds max 100) — surfaced as an explicit toast (not just the disabled
    // button + tooltip) so crossing the limit is never a silent surprise.
    const packages = this.selectedPackageIds.length;
    if (packages > ENRICHMENT_EXPORT_MAX_PO_IDS) {
      this.toast.showError(
        `You can export up to ${ENRICHMENT_EXPORT_MAX_PO_IDS} product offerings at a time — the selected prices come from ${packages}. Deselect some rows to export.`,
        { duration: 4000 },
      );
    }
  }

  /** Mirror the persistent selection into the `[checked]` input so a freshly
   *  loaded page re-checks its rows. */
  private refreshCheckedRows(): void {
    this.checkedRows = Array.from(this.selected.values());
  }

  // ── Bulk "Assign shelves" ──────────────────────────────────────────────────
  /** Live selection size (the persisted map survives pagination). Drives the
   *  contextual action bar's visibility + count. */
  get selectionCount(): number {
    return this.selected.size;
  }
  get hasSelection(): boolean {
    return this.selected.size > 0;
  }
  /** Selected rows, in pick order. */
  get selectedRows(): EnrichmentPriceRow[] {
    return Array.from(this.selected.values());
  }

  /** The packages the selection spans, each once, in pick order — what
   *  Export sends and what its cap counts. */
  get selectedPackageIds(): string[] {
    return [...new Set(this.selectedRows.map((row) => row.offeringId))];
  }

  /** Distinct prices selected: a price shared by two selected packages is
   *  two rows but one price. */
  get selectedPriceCount(): number {
    return new Set(this.selectedRows.map((row) => row.priceId)).size;
  }

  /** The bulk assign needs a channel scope (channel-scoped catalog + per-row
   *  `x-channel` writes). Only the ApplicationOwner case can lack one — every
   *  other role carries the nav-header channel. When true, the bar nudges the
   *  user and the action opens the channel switcher instead of the drawer. */
  get assignNeedsChannel(): boolean {
    return !this.isChannelSelected;
  }

  /** The channel precondition for Export is ROLE-DEPENDENT, mirroring the
   *  BFF's `resolveImportChannel` contract exactly (verified live: a bound
   *  Channel-Owner call needs no `x-channel` — the backend resolves the
   *  channel from the JWT and ignores the header if sent; an unscoped
   *  ApplicationOwner/AnyChannel call MUST send a single mapped `x-channel`,
   *  else 400 `channel_required`):
   *   - bound role (`!isApplicationOwnerChannel`) → always satisfied, no
   *     channel needed.
   *   - unscoped ApplicationOwner role → satisfied only once a channel is
   *     picked (`isChannelSelected`).
   *  An earlier revision dropped this precondition entirely ("≥1 row
   *  selected" only), which let an ApplicationOwner click Export with no
   *  channel picked and guaranteed a 400 — reported live. This restores the
   *  precondition, but ONLY for the unscoped case; a bound role is never
   *  blocked by it (that was the original, correct behavior this page
   *  already had before bulk export existed). */
  get exportChannelRequirementMet(): boolean {
    return !this.isApplicationOwnerChannel || this.isChannelSelected;
  }

  /** Export is enabled by row selection (checkbox column driven by
   *  `canSelectRowsForBulkActions`, reachable by every role holding
   *  `enrichment:export` — including bound Channel-Owner roles, who now get
   *  a selection UI on this page whatever the channel state) AND the
   *  role-dependent channel
   *  precondition above. There is no more "export the current page"
   *  fallback — selection is now always reachable, so the fallback that
   *  existed only to cover the no-selection-UI gap is gone. */
  get canExportSelection(): boolean {
    return (
      this.hasSelection &&
      this.selectedPackageIds.length <= ENRICHMENT_EXPORT_MAX_PO_IDS &&
      this.exportChannelRequirementMet
    );
  }

  /** The workbook is per package, so the label counts packages, not rows. */
  get exportButtonLabel(): string {
    if (!this.hasSelection) return "Export";
    const n = this.selectedPackageIds.length;
    return `Export ${n} ${n === 1 ? "product offering" : "product offerings"}`;
  }

  /** Tooltip explaining why Export is disabled, or what it will do. */
  get exportButtonTitle(): string {
    if (!this.hasSelection)
      return "Select prices to export their product offerings";
    if (this.selectedPackageIds.length > ENRICHMENT_EXPORT_MAX_PO_IDS) {
      return `Select prices from ${ENRICHMENT_EXPORT_MAX_PO_IDS} or fewer product offerings to export`;
    }
    if (!this.exportChannelRequirementMet) return "Select a channel first";
    return "Export the product offerings of the selected prices";
  }

  /** The bulk bar's lifecycle buttons, each with how many distinct selected
   *  prices it would change. */
  get bulkLifecycleActions(): {
    action: LifecycleAction;
    menuId: string;
    label: string;
    eligible: number;
    disabled: boolean;
    tooltip: string;
  }[] {
    const rows = this.selectedRows;
    const total = this.selectedPriceCount;
    const sending = this.transitionSending;
    return this.availableLifecycleActions().map((action) => {
      const spec = LIFECYCLE_ACTIONS[action];
      const plan = planTransitions(rows, action, this.openSchedules);
      const eligible = plannedPriceCount(plan);
      let tooltip = whyNoneEligible(plan, action, this.currentChannelLabel);
      if (sending) tooltip = "Wait until the change being sent finishes";
      else if (eligible) {
        tooltip = `${spec.label}: ${eligible} of ${total} selected ${total === 1 ? "price" : "prices"}`;
      }
      return {
        action,
        menuId: spec.menuId,
        label: spec.label,
        eligible,
        disabled: sending || !eligible,
        tooltip,
      };
    });
  }

  /** Open the confirm drawer for `action` over `rows`: the row menu passes
   *  one row, the bulk bar the selection. */
  openTransition(
    action: LifecycleAction,
    rows: readonly EnrichmentPriceRow[],
  ): void {
    if (
      !this.isChannelSelected ||
      rows.length === 0 ||
      this.transitionSending
    ) {
      return;
    }
    this.closeTransition();
    this.transitionAction = action;
    this.transitionRows = [...rows];
    this.transitionChannel = this.selectedChannel;
    this.transitionScope = this.writeScope();
    const ref = this.drawerService.showDrawerRef(
      this.transitionDrawerTemplate,
      {
        title: LIFECYCLE_ACTIONS[action].label,
        description: this.currentChannelLabel,
        width: "520px",
        position: "right",
        showCloseButton: true,
        backdropDismiss: true,
      },
    );
    this.transitionDrawerRef = ref;
    // X / backdrop / Escape ERROR the result stream; a programmatic close()
    // completes it. The ref check keeps a late event from an older drawer
    // from tearing down a newer one.
    const cleanup = (): void => {
      if (this.transitionDrawerRef !== ref) return;
      this.resetTransition();
    };
    ref.result.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      complete: cleanup,
      error: cleanup,
    });
  }

  closeTransition(): void {
    const ref = this.transitionDrawerRef;
    this.resetTransition();
    ref?.close();
  }

  private resetTransition(): void {
    this.transitionDrawerRef = null;
    this.transitionAction = null;
    this.transitionRows = [];
    this.transitionChannel = "";
    this.transitionScope = "";
    this.transitionOutcomes = null;
  }

  onTransitionRowsRefreshed({ rows, gone }: TransitionRowsRefresh): void {
    for (const row of rows) {
      if (this.selected.has(row.key)) this.selected.set(row.key, row);
    }
    for (const row of gone) this.selected.delete(row.key);
    this.refreshCheckedRows();
  }

  onTransitionConfirmed({ plan, scheduledAt }: TransitionConfirmation): void {
    const action = this.transitionAction;
    const ref = this.transitionDrawerRef;
    if (!action || !ref || this.transitionSending) return;
    const channel = this.transitionChannel;
    const scope = this.transitionScope;
    this.transitionSending = true;
    // Not tied to the page's lifetime: leaving mid-run must neither abort the
    // requests in flight nor drop the ones still waiting.
    sendTransitions(this.enrichmentService, plan, action, scheduledAt, {
      channel,
      holds: () => this.writeScope() === scope,
    }).subscribe((outcomes) =>
      this.onTransitionSent(ref, action, channel, outcomes),
    );
  }

  private onTransitionSent(
    ref: DrawerRef<unknown>,
    action: LifecycleAction,
    channel: string,
    outcomes: readonly TransitionOutcome[],
  ): void {
    this.transitionSending = false;
    if (this.destroyRef.destroyed) {
      this.reportTransition(action, channel, outcomes);
      return;
    }
    if (this.transitionDrawerRef === ref) this.transitionOutcomes = outcomes;
    else this.reportTransition(action, channel, outcomes);
    const changed = new Set(
      outcomes.filter((o) => o.ok).flatMap((o) => o.priceIds),
    );
    for (const row of this.selectedRows) {
      if (changed.has(row.priceId)) this.selected.delete(row.key);
    }
    this.refreshCheckedRows();
    this.refreshAfterWrite();
  }

  private reportTransition(
    action: LifecycleAction,
    channel: string,
    outcomes: readonly TransitionOutcome[],
  ): void {
    let summary = outcomeSummary(
      action,
      outcomes,
      this.channelDirectory.getLabel(channel),
    );
    if (outcomes.some((o) => o.message === SCOPE_CHANGED)) {
      summary += ` ${SCOPE_CHANGED_REASON}`;
    }
    if (outcomes.every((o) => o.ok)) {
      this.toast.showSuccess(summary, { duration: 4000 });
    } else {
      this.toast.showError(summary, { duration: 6000 });
    }
  }

  /** The role and channel a bulk write lands in. The BFF takes a bound
   *  role's channel from the token the request carries, which moves before
   *  the role on screen after a switch in another tab, and an App Owner's
   *  from x-channel. A run planned in one scope must not go on in another. */
  private writeScope(): string {
    return writeScopeKey(
      this.roleService.selectedRole()?.roleId,
      this.authService.getAccessToken(),
      this.enrichmentState.getChannel(),
    );
  }

  /** Drop the whole selection (the action bar's "Clear" + post-assign reset). */
  clearSelection(): void {
    this.selected.clear();
    this.checkedRows = [];
  }

  /** Open the bulk assign drawer for the current selection. Requires
   *  `enrichment:update` — the button is hidden without it, and this re-checks
   *  because the checkbox column can also come from `enrichment:export` alone.
   *  When no channel is resolved yet it opens the channel switcher so the click
   *  is never a dead no-op. */
  openCategoryAssign(): void {
    if (!this.hasSelection || !this.canAssignCategories) return;
    if (this.transitionSending) return;
    if (this.assignNeedsChannel) {
      // Defer: this click bubbles to the `document:click` host listener that
      // closes panels, so opening synchronously would immediately re-close.
      setTimeout(() => (this.channelPanelOpen = true), 0);
      return;
    }
    // Close any existing drawer first. The built-in close (X / backdrop) does
    // not reliably emit `result`, so guarding on a non-null ref would
    // permanently block reopening; closing first also collapses a rapid
    // double-click into a single drawer.
    this.categoryAssignDrawerRef?.close();
    const scope = this.writeScope();
    this.assignScope = {
      channel: this.selectedChannel,
      holds: () => this.writeScope() === scope,
    };
    const prices = this.selectedPriceCount;
    const packages = this.selectedPackageIds.length;
    const ref = this.drawerService.showDrawerRef(
      this.categoryAssignDrawerTemplate,
      {
        title: "Assign shelves",
        description: `${prices} ${prices === 1 ? "price" : "prices"} from ${packages} ${packages === 1 ? "product offering" : "product offerings"}`,
        width: "480px",
        position: "right",
        showCloseButton: true,
        backdropDismiss: true,
      },
    );
    this.categoryAssignDrawerRef = ref;
    // Clear the ref only if THIS drawer is still the active one, so a late
    // close event from a previous drawer can't wipe a newer ref.
    ref.result.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.categoryAssignDrawerRef === ref) {
        this.categoryAssignDrawerRef = null;
      }
    });
  }

  closeCategoryAssign(): void {
    this.categoryAssignDrawerRef?.close();
    this.categoryAssignDrawerRef = null;
  }

  /** The drawer resolved its bulk save: close it, clear the selection,
   *  re-read the page and report the outcome. */
  onCategoryAssignApplied(event: CategoryAssignApplied): void {
    this.closeCategoryAssign();
    this.clearSelection();
    this.refreshAfterWrite();
    this.showAssignSummaryModal(event);
  }

  /** Re-read the current page after a write, and drop the cached unfiltered
   *  baseline so clearing a search refetches it too. A first save on a price
   *  creates its channel overlay, which changes the row's status and
   *  on-channel state — only the BFF knows the result. */
  private refreshAfterWrite(): void {
    this.hasCachedBaseline = false;
    // With nothing listed on this channel, re-send the term the last load
    // carried, never a draft still in the box.
    const term = this.listed ? this.listed.term : this.inFlightTerm;
    this.resetPackageCount();
    this.loadPrices(term, this.config.currentPage);
  }

  private showAssignSummaryModal(event: CategoryAssignApplied): void {
    const { summary, addedShelves } = event;
    const catN = addedShelves.length;
    const catWord = catN === 1 ? "shelf" : "shelves";
    const priceN = summary.pricesUpdated;
    const priceWord = priceN === 1 ? "price" : "prices";
    const touched = summary.outcomes.filter(
      (o) => (o.pricesUpdated ?? 0) > 0,
    ).length;
    const unchangedN = summary.pricesUnchanged;
    const unchangedTail =
      unchangedN > 0
        ? ` ${unchangedN} ${unchangedN === 1 ? "price" : "prices"} already had ${
            catN === 1 ? "this shelf" : "these shelves"
          }.`
        : "";
    const failTail =
      summary.pricesFailed > 0
        ? ` ${summary.pricesFailed} price(s) could not be updated — please try again.`
        : "";
    const notSentN = summary.pricesNotSent;
    const notSentTail =
      notSentN > 0
        ? ` ${notSentN} ${notSentN === 1 ? "price was" : "prices were"} not sent. ${SCOPE_CHANGED_REASON}`
        : "";
    const clean = summary.failed === 0;
    this.omniarkModal.showModal({
      title: clean ? "Shelves assigned" : "Partially assigned",
      message:
        `Added ${catN} ${catWord} to ${priceN} ${priceWord} ` +
        `across ${touched} product offering(s).${unchangedTail}${failTail}${notSentTail}`,
      primaryButtonText: "OK",
      primaryButtonVariant: summary.failed === 0 ? "primary" : "error",
      showCloseButton: false,
      backdropDismiss: true,
    });
  }

  // ── Sort ───────────────────────────────────────────────────────────────────
  private applySortChange(): void {
    this.config = { ...this.config, currentPage: 1 };
    this.loadPrices(this.searchTerm || undefined, 1);
  }

  /** The status pill's text: the canonical label, else the raw wire value. */
  statusLabel(row: EnrichmentPriceRow): string {
    if (row.status) return LIFECYCLE_LABEL[row.status];
    return row.raw.lifecycleStatus?.trim() || "—";
  }

  packageTone(pkg: PackageStatusView): { dot: string; text: string } {
    return PACKAGE_TONE_CLASS[pkg.tone];
  }

  packageStatusTip(row: EnrichmentPriceRow, pkg: PackageStatusView): string {
    const base =
      "The product offering’s own status. It covers every price in this product offering. Each price has its own status per channel, shown under Status.";
    switch (pkg.tone) {
      case "sellable": {
        const sellable = `${base} Launched means the product offering is sellable: customers can buy it.`;
        return row.status === "launched"
          ? sellable
          : `${sellable} This price is ${this.statusLabel(row).toLowerCase()}, so it is not sellable yet.`;
      }
      case "approved":
        return `${base} Active means the product offering is approved in the catalog but not launched, so customers cannot buy it yet.`;
      case "offSale":
        return row.status === "active" || row.status === "launched"
          ? `${base} This price is live, but its product offering is ${pkg.label.toLowerCase()}.`
          : base;
      case "preparing":
        return base;
    }
  }

  /** CSS-safe per-value class suffix, e.g. `status-designed` / `status-rejected`.
   *  The suffix is the row's own status lowercased, so it always tracks the
   *  TMF620 canonical spelling — keep the `.status-*` rules in the SCSS named to
   *  match, or the pill silently falls back to an uncoloured base style (which
   *  is exactly what `.status-reject` did once packages could reach that
   *  state). */
  statusClass(row: EnrichmentPriceRow): string {
    return `status-${(row.status || "unknown").toLowerCase()}`;
  }

  shelfNames(row: EnrichmentPriceRow): string {
    return row.shelves.map((s) => s.name).join(", ") || "—";
  }

  // ── Columns popover ────────────────────────────────────────────────────────
  toggleColumnsPanel(event: MouseEvent): void {
    event.stopPropagation();
    this.columnsPanelOpen = !this.columnsPanelOpen;
    // Opening: stage a fresh copy of the committed state so edits start from
    // what's on screen and are discarded if the user closes without Apply.
    if (this.columnsPanelOpen) this.seedPendingColumns();
  }

  /** Copy the committed column state into the pending (staged) buffers. */
  private seedPendingColumns(): void {
    this.pendingOrder = [...this.columnOrder];
    this.pendingVisible = new Set(this.visibleColumns);
    // Fresh session — never block the first toggle of a key against a stale
    // timestamp from a previous open.
    this.lastToggleAt.clear();
  }

  isColumnVisible(key: string): boolean {
    // Reflects the PENDING state — the picker checkboxes preview the edit; the
    // table only changes on Apply.
    return this.pendingVisible.has(key);
  }

  isColumnLocked(key: string): boolean {
    return isColumnLockedFn(key);
  }

  toggleColumn(key: string): void {
    if (this.isColumnLocked(key)) return;
    // Defensive: the enr-checkbox fires `(change)` TWICE for a single click —
    // its <label> both wraps AND `for`-targets the <input>, so the click
    // double-activates the control. A naive flip would remove then re-add the
    // key (net no-op). Collapse the synchronous echo so one click = one toggle.
    // Swallow the enr-checkbox's duplicate `(change)` echo: a second change for
    // the same key within CHECKBOX_ECHO_MS is the double-activation, not a real
    // re-toggle. Without this the flip round-trips and the change is lost.
    const now = Date.now();
    if (
      now - (this.lastToggleAt.get(key) ?? 0) <
      EnrichmentList.CHECKBOX_ECHO_MS
    ) {
      return;
    }
    this.lastToggleAt.set(key, now);
    // Stage the visibility change only — `pendingOrder` is untouched (the row
    // stays put) and the table is unchanged until Apply.
    const next = new Set(this.pendingVisible);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.pendingVisible = next;
  }

  resetColumns(): void {
    // Stage the defaults; the user still confirms with Apply.
    this.pendingVisible = new Set(DEFAULT_VISIBLE_COLUMNS);
    this.pendingOrder = this.buildColumnOrder(DEFAULT_VISIBLE_COLUMNS);
  }

  // ── Role gate ──────────────────────────────────────────────────────────────
  /** True when the nav-header role channel is the special "ApplicationOwner"
   *  channel — that pick means "no specific channel scope", so the user gets
   *  the in-page channel switcher + parent-only toggle to drill in. For every
   *  other channel pick, the page is locked to that channel and reads
   *  `x-channel` straight from the nav-header value. */
  get isApplicationOwnerChannel(): boolean {
    // Read the SELECTED ROLE's available channels, not the resolved
    // `activeChannelName()`. The latter goes through `selectedChannelId`, which
    // can go stale / mis-resolve across a role switch (a leftover channel id
    // resolves to a different channel), hiding the switcher even though the
    // active role is still the synthetic "ApplicationOwner". The role's channel
    // set is the reliable signal (mirrors banner-management's check).
    return this.roleService
      .availableChannelsForSelectedRole()
      .some((c) => c.channelName === "ApplicationOwner");
  }

  /** Mirror the active role's channel (nav header) into the enrichment store
   *  so the `x-channel` header is sent for channel-scoped picks. The
   *  ApplicationOwner channel doesn't define a real scope, so we don't
   *  overwrite the user's local channel choice in that case. */
  private syncRoleChannelIntoStore(reload = true): void {
    if (this.isApplicationOwnerChannel) {
      // An App Owner's channel pick is a transient drill-in, not a saved
      // scope. A stale channel restored from localStorage on refresh would
      // leave the pill showing a channel while the list is parent-only and
      // the toggle is disabled — an inconsistent state. Reset to "no channel"
      // on (re)entry so the list always starts clean at the parent/master view.
      if (this.enrichmentState.getChannel() !== "") {
        this.enrichmentState.setChannel("");
      }
      this.selectedChannel = "";
      this.resetPriceStatusTab();
      return;
    }
    // Prefer the active channel's `channelKey` so the value mirrored into the
    // store matches the picker's channelKey identity, but FALL BACK to
    // `channelName` — role-profile channels do not carry a key.
    //
    // The BFF's role payload (internal/role/model.go `EnrChannel`) emits only
    // channelId / channelName / channelDesc; `channelKey` reaches the portal
    // only on the channel MASTER (GET /api/v1/channel), which feeds the admin
    // picker. So `activeChannel().channelKey` is always undefined and this
    // resolved to "" for every channel-bound role — no x-channel was sent and
    // the list silently fell back to the backend's un-narrowed view. Reading
    // channelKey here was a deliberate migration away from channelName; the
    // key just isn't available on this path, which is why the sibling category
    // screens already spell the fallback out (`ch.channelKey ?? ch.channelName`).
    //
    // Sending the NAME is safe: the backend maps x-channel through
    // channelmap.ToKey, which resolves a sale name to its key and passes an
    // already-key value straight through.
    const next = this.roleChannelValue();
    if (this.enrichmentState.getChannel() === next) {
      this.selectedChannel = next;
      return;
    }
    this.enrichmentState.setChannel(next);
    this.selectedChannel = next;
    this.resetPriceStatusTab();
    this.resetForScopeChange();
    if (reload) this.loadPrices(this.searchTerm?.trim() || undefined, 1);
  }

  /** The nav-header channel as an `x-channel` value: its key when the payload
   *  carries one, otherwise its sale name (see syncRoleChannelIntoStore for why
   *  the key is usually absent). Empty when no channel is resolved at all. */
  private roleChannelValue(): string {
    const channel = this.roleService.activeChannel();
    if (!channel) return "";
    return channel.channelKey?.trim() || channel.channelName?.trim() || "";
  }

  // ── Channel selector ───────────────────────────────────────────────────────
  get isChannelSelected(): boolean {
    return this.selectedChannel !== "";
  }

  /** A picked channel travels as `x-channel`, and the App Owner with none gets
   *  the master view through `parentOnly`. Any other role with no channel is
   *  not asked for at all. Without either param an AnyChannel caller gets every
   *  channel's overlays beside the masters, so one (offering, price) pair
   *  repeats. `parentOnly` is no way out either: for a bound caller the BFF
   *  drops the channel filter and returns every master (productoffering
   *  service.go `case parentOnly:`, mongodoc channeloverlay.go). */
  get listScope(): ListScope {
    if (this.isChannelSelected) return "channel";
    return this.isApplicationOwnerChannel ? "masters" : "none";
  }

  /** Gate for the "Assign shelves" bulk action — the PERMISSION to edit
   *  enrichments, nothing else. Binding shelves is an edit, so `enrichment:update`
   *  is the honest gate; a role that may open a package and change its shelves
   *  one at a time may do the same in bulk. The channel requirement belongs at
   *  ACTION time, where {@link assignNeedsChannel} opens the channel picker
   *  instead of the drawer. */
  get canAssignCategories(): boolean {
    return this.permissionService.hasPermission(Permissions.EnrichmentUpdate);
  }

  /** Gate for the enr-table `selectable` (checkbox column) config. One
   *  selection serves every bulk action, and the bar shows only the actions
   *  the role holds, so any of their permissions opens the column. */
  get canSelectRowsForBulkActions(): boolean {
    return [
      Permissions.EnrichmentUpdate,
      Permissions.EnrichmentExport,
      ...LIFECYCLE_ACTION_ORDER.map(
        (action) => LIFECYCLE_ACTIONS[action].permission,
      ),
    ].some((permission) => this.permissionService.hasPermission(permission));
  }

  get currentChannelLabel(): string {
    return this.channelDirectory.getLabel(this.selectedChannel);
  }

  get groupedChannels(): { group: ChannelGroup; items: ChannelMeta[] }[] {
    const q = this.channelSearch.trim().toLowerCase();
    const all = this.channelDirectory.channels();
    const matches = q
      ? all.filter(
          (m) =>
            m.label.toLowerCase().includes(q) ||
            m.value.toLowerCase().includes(q),
        )
      : all;
    // Group order follows first appearance in the API response so the BE
    // controls the ordering without a FE deploy.
    const groups: ChannelGroup[] = [];
    for (const m of matches) {
      if (!groups.includes(m.group)) groups.push(m.group);
    }
    return groups.map((group) => ({
      group,
      items: matches.filter((m) => m.group === group),
    }));
  }

  toggleChannelPanel(event: MouseEvent): void {
    event.stopPropagation();
    this.channelPanelOpen = !this.channelPanelOpen;
    if (!this.channelPanelOpen) this.channelSearch = "";
  }

  selectChannel(value: string): void {
    this.channelPanelOpen = false;
    this.channelSearch = "";
    if (value === this.selectedChannel && this.listed) return;
    this.selectedChannel = value;
    // Store handles localStorage write-through, so callers don't.
    this.enrichmentState.setChannel(value);
    this.resetPriceStatusTab();
    this.resetForScopeChange();
    this.loadPrices(this.searchTerm?.trim() || undefined, 1);
  }

  clearChannel(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.isChannelSelected) return;
    this.channelPanelOpen = false;
    this.channelSearch = "";
    this.selectedChannel = "";
    this.enrichmentState.setChannel("");
    this.resetPriceStatusTab();
    this.resetForScopeChange();
    this.loadPrices(this.searchTerm?.trim() || undefined, 1);
  }

  get activeStatusTab(): PriceStatusTabSpec {
    return PRICE_STATUS_TAB[this.priceStatusTab];
  }

  get shownStatusTab(): PriceStatusTabSpec {
    return PRICE_STATUS_TAB[this.shown?.tab ?? this.priceStatusTab];
  }

  get isPriceStatusTabDisabled(): boolean {
    return !this.isChannelSelected;
  }

  tabTitle(tab: PriceStatusTabSpec): string {
    return this.isPriceStatusTabDisabled
      ? "Pick a channel first. A price has its own status on each channel."
      : tab.tooltip;
  }

  private resetPriceStatusTab(): void {
    this.priceStatusTab = "all";
    this.searchNotice = null;
  }

  selectPriceStatusTab(tab: PriceStatusTab): void {
    if (this.isPriceStatusTabDisabled) return;
    if (this.priceStatusTab === tab) {
      if (this.shownHere?.tab !== tab && !this.loading) {
        this.loadPrices(this.searchTerm || undefined, this.config.currentPage);
      }
      return;
    }
    const textCleared = tab !== "all" && !!this.searchTerm.trim();
    if (textCleared) this.dropSearchTerm();
    this.priceStatusTab = tab;
    this.searchNotice = textCleared ? "textCleared" : null;
    this.resetForScopeChange();
    this.loadPrices(undefined, 1);
  }

  /** Drag-drop reorder handler for the column visibility popover. Mirrors the
   *  online-store dashboard's `onColumnDrop` — moves the dragged item in the
   *  master `allColumns`-derived list and persists the new order so subsequent
   *  toggles render in the user's preferred order. */
  onColumnDrop(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    // Stage the reorder only — drag indices map to the rendered picker list
    // (`reorderableColumns`), which already mirrors `pendingOrder`. The table
    // re-orders on Apply.
    const rendered = this.reorderableColumns.map((c) => c.key);
    moveItemInArray(rendered, event.previousIndex, event.currentIndex);
    this.pendingOrder = rendered;
  }

  /** Apply button — commit the staged picker edits (visibility + drag order)
   *  to the table and persistence, then close. This is the ONLY place the
   *  table's columns actually change; toggles/drags before it are previews. */
  applyColumnFilter(): void {
    this.columnOrder = [...this.pendingOrder];
    this.visibleColumns = this.orderVisible(this.pendingVisible);
    saveColumnPreferences(this.visibleColumns);
    this.columnsPanelOpen = false;
  }

  // ── Generic helpers ────────────────────────────────────────────────────────
  /** Split the combined product code / number textarea into the two backend
   *  params by prefix (case-insensitive): a token starting with `O` is a
   *  productCode, `P` a productNumber; tokens starting with neither are ignored.
   *  Splits on commas / whitespace / new lines and returns comma-joined lists. */
  private parseCodeRefs(raw: string | undefined): {
    productCode: string;
    productNumber: string;
  } {
    const tokens = splitCodeTokens(raw);
    return {
      productCode: tokens.filter((t) => /^o/i.test(t)).join(","),
      productNumber: tokens.filter((t) => /^p/i.test(t)).join(","),
    };
  }

  // ── Data loading ───────────────────────────────────────────────────────────
  private buildFilterRequest(
    searchTerm: string | undefined,
    page: number,
  ): {
    params: EnrichedSearchProductOfferingParams;
    /** The box's term as sent, whether it went out as `q` or as `priceId`. */
    sentTerm: string | undefined;
  } {
    const normalizedSearchTerm = this.normalizeSearchTerm(searchTerm);
    const params: EnrichedSearchProductOfferingParams = {
      pageSize: this.config.pageSize,
      page,
    };
    let sentTerm: string | undefined;
    // Free-text term — the backend's `q` runs a case-insensitive substring
    // match across the offering id/name/description AND its price (POP)
    // id/name/description. It does not match a product code or number; those
    // need the productCode / productNumber filters below. Forward only the
    // filters `GET /enrichment/price` supports; unknown keys are rejected by
    // the backend with HTTP 400.
    const f = this.appliedFilters;
    if (f) {
      const type = f.productType?.trim();
      if (type) params.productType = type;
      const line = f.productLine?.trim();
      if (line) params.productLine = line;
      const version = f.version?.trim();
      if (version) params.version = version;
      const lifecycle = f.lifecycleStatus?.trim();
      if (lifecycle) params.lifecycleStatus = lifecycle;
      if (f.isSellable === "true") params.isSellable = true;
      else if (f.isSellable === "false") params.isSellable = false;
      if (f.deleted === "only") params.deleted = "only";
      const { productCode, productNumber } = this.parseCodeRefs(f.productRefs);
      if (productCode) params.productCode = productCode;
      if (productNumber) params.productNumber = productNumber;
      // Exact-match offering id(s) — comma list, backend expands to a `$in`
      // on the offering document itself (no POP pre-query, never truncates).
      const offeringId = splitCodeTokens(f.offeringCode).join(",");
      if (offeringId) params.id = offeringId;
      // Exact-match price (POP) id(s): returns exactly those prices.
      const priceId = splitCodeTokens(f.priceId).join(",");
      if (priceId) params.priceId = priceId;
    }

    const scope = this.listScope;
    const tabQuery = statusTabQuery(this.activeStatusTab);
    if (scope === "channel" && tabQuery) Object.assign(params, tabQuery);

    // `q` and productCode/productNumber are mutually exclusive (the backend
    // rejects the combination). The offering-id and price-id filters are
    // exact-match like the code filters, and `priceLifecycleStatus` shares
    // the same BE rule, so they all follow it: any of them wins and the
    // free-text term is sent only when none is active. For `id` / `priceId`
    // that is a PORTAL convention, not a backend constraint — the backend
    // composes either with `q` quite happily (only productCode /
    // productNumber actually 400) — but one consistent "a code search clears
    // the text box" rule beats two subtly different ones.
    if (
      !params.id &&
      !params.priceId &&
      !params.productCode &&
      !params.productNumber &&
      !params.priceLifecycleStatus &&
      normalizedSearchTerm
    ) {
      // A term that is price ids (`op_…`) goes to the exact price-id filter
      // straight away: a direct lookup, where `q` would scan every text
      // field for it.
      const priceIds = priceIdSearch(normalizedSearchTerm);
      if (priceIds) params.priceId = priceIds;
      else params.q = normalizedSearchTerm;
      sentTerm = normalizedSearchTerm;
    }

    if (scope === "masters") params.parentOnly = true;

    const sortParam = buildSortParam(this.sort);
    if (sortParam) {
      params.sortBy = sortParam.sortBy;
      params.sortDir = sortParam.sortDir;
    }

    return { params, sentTerm };
  }

  private loadPrices(searchTerm?: string, page = 1): void {
    this.codeSearchOffer = null;
    this.allStatusesOffer = false;
    const term = this.sendableTerm(searchTerm);

    // Last request wins: abort whatever is still in flight before firing the
    // next one, so responses can never arrive out of order (rapid typing,
    // Enter mid-debounce, quick page/channel switches).
    this.cancelInFlightLoad();
    const scope = { channel: this.selectedChannel, tab: this.priceStatusTab };
    if (this.listScope === "none") {
      this.resetPackageCount();
      this.countQuery = null;
      this.loading = false;
      this.searchTruncated = false;
      this.tableData = [];
      this.shown = { ...scope, term: undefined };
      this.config = { ...this.config, totalItems: 0, currentPage: 1 };
      return;
    }
    this.loading = true;
    // Send x-channel whenever a channel is resolved, for EVERY role.
    //
    // This used to be gated on isApplicationOwnerChannel, on the assumption
    // that a non-ApplicationOwner role is always channel-bound and therefore
    // has its channel resolved from the token. That assumption is wrong: the
    // backend derives boundness from ENR_ROLE.CHANNEL_ID (go-cxa
    // rbac/repository.go), and a role whose CHANNEL_ID is NULL is AnyChannel
    // even though the portal shows it a concrete channel list. Such a caller
    // sent no channel at all and landed on the backend's un-narrowed
    // `channel == ""` branch — every channel's masters AND overlays, silently
    // out of scope. Sending the header unconditionally is safe: a genuinely
    // bound caller's header is IGNORED (resolveChannelScope returns the token
    // channel first, so the tenant boundary is not client-bypassable).
    const xChannel = this.isChannelSelected ? this.selectedChannel : null;
    const { params, sentTerm } = this.buildFilterRequest(term, page);
    const requestCount = countQuery(params, xChannel);
    if (requestCount.key !== this.countQuery?.key) this.resetPackageCount();
    const request: ShownRows = { ...scope, term: sentTerm };
    this.inFlightTerm = sentTerm;
    this.loadRequest = this.productOfferingService
      .getEnrichmentPrices(params, xChannel)
      .subscribe({
        next: (response: EnrichmentPriceListResponse) => {
          // Re-read on every load so a refined search that no longer hits the
          // backend's POP pre-query cap clears the banner.
          this.searchTruncated = response.searchTruncated === true;
          // The Go enricher envelope uses { code, desc, data } while older
          // services use { resultCode, resultDesc, data }. Accept either —
          // we reach this branch only on HTTP 2xx, so a populated `data`
          // is enough to render.
          const code =
            response.resultCode ??
            (response as unknown as { code?: string }).code;
          const isSuccess = code === "20000" || code === "200";
          if (!isSuccess || !response.data) {
            this.loading = false;
            this.focusSearchInput();
            return;
          }

          this.tableData = toPriceRows(response.data, {
            channelSelected: this.isChannelSelected,
            now: Date.now(),
          });
          this.shown = request;
          this.countQuery = requestCount;
          const empty = this.tableData.length === 0;
          const absent = empty && !this.searchTruncated;
          this.codeSearchOffer =
            params.q && absent && looksLikeProductCodes(params.q)
              ? params.q
              : null;
          this.allStatusesOffer =
            absent && !!params.priceLifecycleStatus && sentCodeScope(params);

          const totalItems = response.pagination?.totalItems ?? 0;
          const totalPages = response.pagination?.totalPages ?? 0;
          const currentPage = response.pagination?.page ?? page;
          const pageSize =
            response.pagination?.pageSize ?? this.config.pageSize;

          const lastPage = Math.max(
            1,
            totalPages || Math.ceil(totalItems / pageSize),
          );
          if (page > lastPage && empty) {
            this.tableConfig = {
              ...this.tableConfig,
              currentPage: lastPage,
              pageSize,
            };
            this.loadPrices(term, lastPage);
            return;
          }
          this.config = {
            ...this.config,
            totalItems,
            currentPage,
            pageSize,
          };
          if (empty) {
            this.setEmptyMessage(
              this.emptyMessageFor(params),
              currentPage,
              pageSize,
            );
          }

          if (!term && this.isBaselineView) {
            this.hasCachedBaseline = true;
            this.cachedData = this.tableData;
            this.cachedPagination = {
              page: currentPage,
              pageSize,
              totalItems,
              totalPages,
            };
          }

          // A selected row on this page takes the fresh data, so the bulk
          // bar's eligibility reads the status the BFF just returned.
          for (const row of this.tableData) {
            if (this.selected.has(row.key)) this.selected.set(row.key, row);
          }
          this.refreshCheckedRows();
          this.loadPageSchedules();
          this.loading = false;
          if (this.returnScroll) {
            this.tableConfig = {
              ...this.tableConfig,
              currentPage,
              pageSize,
            };
            const scroll = this.returnScroll;
            this.returnScroll = null;
            // Wait for the refreshed rows to render before restoring the
            // scrolling layout; autofocus would pull the user to the toolbar.
            setTimeout(() => {
              if (this.destroyRef.destroyed) return;
              const container = this.scrollContainer();
              if (container) {
                container.scrollTop = scroll.top;
                container.scrollLeft = scroll.left;
              }
            });
          } else {
            this.focusSearchInput();
          }
        },
        error: (error) => {
          // Defensive: `q` free-text search is implemented on the current
          // backend, but an older API build may still answer HTTP 501. If so,
          // degrade quietly (empty table, no modal) rather than erroring out.
          this.searchTruncated = false;
          if ((error as { status?: number })?.status === 501) {
            this.tableData = [];
            this.shown = request;
            this.countQuery = requestCount;
            this.config = {
              ...this.config,
              totalItems: 0,
              currentPage: 1,
            };
            this.loading = false;
            this.focusSearchInput();
            return;
          }
          logger.error("Error loading prices:", error);
          this.loading = false;
          this.omniarkModal.showModal({
            title: "Error",
            message: "Failed to load prices. Please try again.",
            primaryButtonText: "OK",
            primaryButtonVariant: "primary",
            showCloseButton: false,
            backdropDismiss: true,
          });
          this.focusSearchInput();
        },
      });
  }

  private emptyMessageFor(params: EnrichedSearchProductOfferingParams): string {
    const tab = this.activeStatusTab;
    const prices = tab.statuses.length > 0 ? `${tab.label} prices` : "prices";
    if (this.searchTruncated) return `No ${prices} shown`;
    if (params.q) return `No ${prices} match "${params.q}"`;
    if (sentCodeScope(params)) return `No ${prices} match these codes`;
    if (this.drawerFilterCount > 0) return `No ${prices} match these filters`;
    return tab.statuses.length > 0
      ? `No ${prices} on this channel`
      : EMPTY_TABLE_MESSAGE;
  }

  /** A new `[config]` object resets enr-table's paginator to its
   *  `currentPage` and `pageSize`. */
  private setEmptyMessage(
    emptyMessage: string,
    currentPage: number,
    pageSize: number,
  ): void {
    if (this.tableConfig.emptyMessage === emptyMessage) return;
    this.tableConfig = {
      ...this.tableConfig,
      emptyMessage,
      currentPage,
      pageSize,
    };
  }

  /** Abort the in-flight list request, if any — unsubscribing an HttpClient
   *  observable cancels the underlying XHR, so the stale response neither
   *  costs the backend nor reaches the `next` handler. */
  private cancelInFlightLoad(): void {
    this.loadRequest?.unsubscribe();
    this.loadRequest = null;
    this.scheduleRequest?.unsubscribe();
    this.scheduleRequest = null;
  }

  private normalizeSearchTerm(searchTerm?: string): string | undefined {
    const trimmed = searchTerm?.trim();
    return trimmed ? trimmed : undefined;
  }

  private isSearchTermTooShort(searchTerm?: string): boolean {
    return !!searchTerm && searchTerm.length < EnrichmentList.MIN_SEARCH_LENGTH;
  }

  private sendableTerm(searchTerm?: string): string | undefined {
    const term = this.normalizeSearchTerm(searchTerm);
    return this.isSearchTermTooShort(term) ? undefined : term;
  }

  private focusSearchInput(): void {
    if (!this.searchInput?.nativeElement) return;
    const inputElement: HTMLInputElement | null =
      this.searchInput.nativeElement.tagName === "INPUT"
        ? (this.searchInput.nativeElement as HTMLInputElement)
        : this.searchInput.nativeElement.querySelector("input");
    if (inputElement && document.activeElement !== inputElement) {
      setTimeout(() => inputElement.focus(), 0);
    }
  }

  // ── Bulk import ──────────────────────────────────────────────────────────
  onImportClick(): void {
    this.importFileInput?.nativeElement.click();
  }

  onImportFileSelected(event: Event): void {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    const file = target.files?.[0];
    target.value = "";
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      this.toast.showError("Please select an Excel (.xlsx) file", {
        duration: 3000,
      });
      return;
    }

    this.pendingImportFile = file;
    this.importDryRun = false;
    this.importOptionsModalRef?.close();
    this.importOptionsModalRef = this.omniarkModal.showTemplateModalWithRef(
      this.importOptionsModalTemplate,
      {
        title: this.isOnlineStoreScope
          ? "Import Online Store packages"
          : "Import enrichment workbook",
        width: "440px",
        backdropDismiss: false,
        showCloseButton: true,
      },
    );
    this.importOptionsModalRef.result.subscribe(() => {
      this.importOptionsModalRef = null;
      this.pendingImportFile = null;
    });
  }

  cancelImportOptions(): void {
    this.importOptionsModalRef?.close();
    this.importOptionsModalRef = null;
    this.pendingImportFile = null;
  }

  /** Confirm from the pre-import modal — uploads the picked file with the
   *  staged dryRun choice. A dry run never touches loadPrices
   *  (nothing was written); a real import with no failed groups refreshes
   *  the current page so saved edits show up immediately. */
  confirmImportOptions(): void {
    const file = this.pendingImportFile;
    if (!file) return;
    const dryRun = this.importDryRun;
    this.importOptionsModalRef?.close();
    this.importOptionsModalRef = null;

    if (this.isOnlineStoreScope) {
      this.importPackages(file, dryRun);
      return;
    }

    const xChannel = this.isApplicationOwnerChannel
      ? this.selectedChannel || null
      : null;

    this.overlayService.showLoading(
      dryRun ? "Validating import..." : "Importing product offerings...",
    );
    this.enrichmentImportExport
      .importWorkbook(file, dryRun, xChannel)
      .pipe(
        catchError((error: unknown) => {
          const report = extractEnrichmentImportReport(error);
          if (report) {
            return of({
              code: "400",
              desc: "Bad Request",
              data: report,
              httpStatus: 400,
            } satisfies EnrichmentImportHttpResponse);
          }
          const message =
            error instanceof HttpErrorResponse && error.status === 403
              ? "You do not have permission to import enrichment data."
              : "Import failed. Please try again.";
          this.toast.showError(message, { duration: 3000 });
          return of(null);
        }),
        finalize(() => {
          this.overlayService.hideLoading();
          this.pendingImportFile = null;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (!response) return;
        const report = response.data;
        if (report.failedGroups > 0) {
          this.showImportReport(report);
          return;
        }
        this.showImportSuccessToast(report);
        if (!dryRun) this.refreshAfterWrite();
      });
  }

  private showImportSuccessToast(report: EnrichmentImportReport): void {
    const message = report.dryRun
      ? `Dry run passed: ${report.processedGroups} of ${report.groups} group(s) validated`
      : `Import complete: ${report.processedGroups} of ${report.groups} group(s) saved`;
    this.toast.showSuccess(message, { duration: 3000 });
  }

  /** Renders the failed groups' violations through the shared
   *  ImportValidationErrorsModal (same "<sheet> · Row N" pattern the
   *  easy-app package import uses), with poId/popId + the stable error code
   *  as the secondary identifier. */
  private showImportReport(report: EnrichmentImportReport): void {
    this.importReportTruncated = report.errorsTruncated;
    this.importReportItemNoun = "validation error";
    const dryRunTag = report.dryRun ? " (dry run — nothing was written)" : "";
    this.importReportSummary = `${report.failedGroups} of ${report.groups} group(s) failed${dryRunTag}`;
    this.importValidationErrors.set(
      report.errors.map((e) => ({
        primary: e.sheet
          ? `${e.sheet} · Row ${e.row}`
          : e.row
            ? `Row ${e.row}`
            : "Group",
        secondary:
          [e.poId, e.popId].filter((v) => !!v).join(" / ") || undefined,
        message: e.code ? `${e.message} (${e.code})` : e.message,
      })),
    );
    this.openImportReportModal("Import failed");
  }

  private openImportReportModal(title: string): void {
    this.importReportModalRef?.close();
    this.importReportModalRef = this.omniarkModal.showTemplateModalWithRef(
      this.importReportModalTemplate,
      {
        title,
        width: "600px",
        showCloseButton: false,
        backdropDismiss: false,
      },
    );
    this.importReportModalRef.result.subscribe(() => {
      this.importReportModalRef = null;
      this.importValidationErrors.set([]);
    });
  }

  closeImportReport(): void {
    this.importReportModalRef?.close();
    this.importReportModalRef = null;
    this.importValidationErrors.set([]);
  }

  private importPackages(file: File, dryRun: boolean): void {
    const xChannel = this.isApplicationOwnerChannel
      ? ONLINE_STORE_X_CHANNEL
      : null;

    this.packageImportStatus.set(
      dryRun
        ? "Validating Online Store packages. This can take several minutes."
        : "Importing Online Store packages. This can take several minutes.",
    );
    this.enrichmentImportExport
      .importPackages(file, dryRun, xChannel)
      .pipe(
        catchError((error: unknown) => {
          const message =
            error instanceof HttpErrorResponse && error.status === 403
              ? "You do not have permission to import enrichment data."
              : error instanceof PackageImportLostError
                ? "Lost track of the import. It may still be running, and packages already saved are kept. Reload the list in a few minutes."
                : (extractPackageImportRefusal(error) ??
                  "Import failed. Please try again.");
          this.toast.showError(message, { duration: 4000 });
          return of(null);
        }),
        finalize(() => {
          this.packageImportStatus.set(null);
          this.pendingImportFile = null;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((report) => {
        if (!report) return;
        this.showPackageImportOutcome(report);
        if (!report.dryRun && report.savedOfferings > 0) {
          this.refreshAfterWrite();
        }
      });
  }

  /** Toast when every row went through clean; otherwise the row list, which
   *  also carries saved rows that came back with warnings. */
  private showPackageImportOutcome(report: PackageImportReport): void {
    const problems = packageImportProblems(report);
    const summary = packageImportSummary(report);
    if (problems.length === 0) {
      this.toast.showSuccess(summary, { duration: 3000 });
      return;
    }
    this.importReportTruncated = false;
    this.importReportItemNoun = "row";
    this.importReportSummary = summary;
    this.importValidationErrors.set(problems);
    this.openImportReportModal(
      packageImportHasFailedRows(report)
        ? "Import finished with errors"
        : "Import finished with warnings",
    );
  }

  // ── Bulk export ──────────────────────────────────────────────────────────
  onExportClick(): void {
    if (!this.canExportSelection) return;
    const poIds = this.selectedPackageIds.filter((id) => !!id);
    if (!poIds.length) return;

    if (this.isOnlineStoreScope) {
      this.openPackageExportOptions();
      return;
    }

    // An explicit header, no interceptor: bound roles send none (the
    // backend resolves their channel from the JWT), an unscoped
    // ApplicationOwner sends the picked channel. exportChannelRequirementMet
    // guarantees `canExportSelection` is already false for an unscoped
    // caller with no channel picked, so this ternary can't silently send an
    // empty/missing header on a click the button claimed was enabled.
    const xChannel = this.isApplicationOwnerChannel
      ? this.selectedChannel || null
      : null;

    this.overlayService.showLoading("Exporting product offerings...");
    this.enrichmentImportExport
      .exportWorkbook(poIds, xChannel)
      .pipe(
        catchError(() => {
          this.toast.showError("Export failed. Please try again.", {
            duration: 3000,
          });
          return of(null);
        }),
        finalize(() => this.overlayService.hideLoading()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((blob) => {
        if (!blob) return;
        const timestamp = new Date().toISOString().replaceAll(/[:.]/g, "-");
        downloadExcelBlob(
          blob,
          `enrichment-import-v1_export_${timestamp}.xlsx`,
        );
        this.toast.showSuccess("Export completed successfully", {
          duration: 3000,
        });
        // Clear the selection on a successful export only — a failed call
        // (catchError above short-circuits to `of(null)`, never reaching
        // here) leaves the user's picks intact so they can retry.
        this.clearSelection();
      });
  }

  private openPackageExportOptions(): void {
    this.packageExportLegacyLayout = false;
    this.packageExportOptionsModalRef?.close();
    this.packageExportOptionsModalRef =
      this.omniarkModal.showTemplateModalWithRef(
        this.packageExportOptionsModalTemplate,
        {
          title: "Export Online Store packages",
          width: "440px",
          backdropDismiss: false,
          showCloseButton: true,
        },
      );
    this.packageExportOptionsModalRef.result.subscribe(() => {
      this.packageExportOptionsModalRef = null;
    });
  }

  cancelPackageExportOptions(): void {
    this.packageExportOptionsModalRef?.close();
    this.packageExportOptionsModalRef = null;
  }

  confirmPackageExportOptions(): void {
    this.cancelPackageExportOptions();
    const poIds = this.selectedPackageIds.filter((id) => !!id);
    if (!this.canExportSelection || !poIds.length) return;
    const format: PackageExportFormat = this.packageExportLegacyLayout
      ? "legacy"
      : "akeneo";

    // Always the header, bound roles included: the BFF picks the export
    // layout from x-channel alone, so without it a bound role gets the
    // master in the editor's layout.
    this.overlayService.showLoading("Exporting Online Store packages...");
    this.enrichmentImportExport
      .exportPackages(poIds, format, ONLINE_STORE_X_CHANNEL)
      .pipe(
        catchError(() => {
          this.toast.showError("Export failed. Please try again.", {
            duration: 3000,
          });
          return of(null);
        }),
        finalize(() => this.overlayService.hideLoading()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((blob) => {
        if (!blob) return;
        const timestamp = new Date().toISOString().replaceAll(/[:.]/g, "-");
        downloadExcelBlob(blob, `enrichment-package_${timestamp}.xlsx`);
        this.toast.showSuccess("Export completed successfully", {
          duration: 3000,
        });
        this.clearSelection();
      });
  }

  onFilter(): void {
    this.filterState.activeSearchTerm.set(this.searchTerm ?? "");
    this.drawerService
      .showDrawer(this.filterDrawerTemplate, {
        title: "Filter prices",
        description:
          "Find prices by exact ID, or narrow them by their product offering's attributes",
        width: "480px",
        position: "right",
        showCloseButton: true,
        backdropDismiss: true,
      })
      .subscribe();
  }

  onFilterApplied(filterData: PackageFilterData): void {
    this.appliedFilters = filterData;
    const { productCode, productNumber } = this.parseCodeRefs(
      filterData.productRefs,
    );
    if (
      productCode ||
      productNumber ||
      filterData.offeringCode?.trim() ||
      filterData.priceId?.trim()
    ) {
      this.dropSearchTerm();
    }
    // Keep the toolbar box honest: `searchScope` already derives itself from
    // `appliedFilters`, so all that is left is seeding the visible draft (and
    // resetting `pickedScope` when the drawer cleared the codes, so the box
    // doesn't sit on an empty code scope the user never chose).
    const committed = this.committedCodeScopes;
    if (committed.length === 1) {
      this.pickedScope = committed[0];
      // The drawer textarea can hold new lines; a single-line box would
      // render those stripped, fusing the codes — seed the normalized list.
      this.codeDraft = normalizeCodeList(filterData[committed[0]]);
    } else if (committed.length === 0) {
      this.pickedScope = "all";
      this.codeDraft = "";
    }
    this.config = { ...this.config, currentPage: 1 };
    this.loadPrices(this.searchTerm || undefined, 1);
  }

  /** The deleted-view banner's "Show active packages": un-tick the deleted
   *  filter and keep every other applied filter. Routed through
   *  `onFilterApplied` — the drawer's own Apply path — so the reset to page 1
   *  and the reload are the same ones a user un-ticking it in the drawer gets. */
  clearDeletedView(): void {
    const f = this.appliedFilters;
    if (!f || f.deleted !== "only") return;
    this.onFilterApplied({ ...f, deleted: "" });
  }

  private clearSearchExclusiveFilters(): void {
    const f = this.appliedFilters;
    if (f && (f.productRefs || f.offeringCode || f.priceId)) {
      this.appliedFilters = {
        ...f,
        productRefs: "",
        offeringCode: "",
        priceId: "",
      };
    }
    if (this.priceStatusTab !== "all") {
      this.priceStatusTab = "all";
      this.searchNotice = "movedToAll";
    }
  }

  onFilterCleared(): void {
    this.appliedFilters = null;
    this.pickedScope = "all";
    this.codeDraft = "";
    this.config = { ...this.config, currentPage: 1 };
    this.loadPrices(this.searchTerm || undefined, 1);
  }

  handleEditAction(item: EnrichmentPriceRow): void {
    this.closePreview();
    // x-channel for the edit GET (and the downstream save PATCH) flows from
    // `EnrichmentStateStore`. For channel-scoped roles the store is already
    // populated by `syncRoleChannelIntoStore` from the nav-header pick — the
    // gate never fires. For the ApplicationOwner case the store carries the
    // in-page pill choice; the gate only opens when that pill is still empty,
    // so the user picks a real channel before editing a parent.
    if (!this.isChannelSelected) {
      this.openChannelGate(item);
      return;
    }
    this.navigateToEdit(item);
  }

  private journeyContext(): string {
    return JSON.stringify([
      this.roleService.selectedRole()?.roleId,
      this.roleChannelValue(),
    ]);
  }

  private scrollContainer(): Element | null {
    return (
      this.host.nativeElement.closest(".page-content") ??
      this.document.scrollingElement
    );
  }

  private captureJourney(): EnrichmentListJourney {
    const container = this.scrollContainer();
    return {
      context: this.journeyContext(),
      entryQuery: this.route.snapshot.queryParamMap.get("q"),
      searchTerm: this.searchTerm,
      filters: this.appliedFilters ? { ...this.appliedFilters } : null,
      sort: { ...this.sort },
      page: this.config.currentPage,
      pageSize: this.config.pageSize,
      pickedScope: this.pickedScope,
      codeDraft: this.codeDraft,
      selectedChannel: this.selectedChannel,
      priceStatusTab: this.priceStatusTab,
      scrollTop: container?.scrollTop ?? 0,
      scrollLeft: container?.scrollLeft ?? 0,
    };
  }

  private restoreJourney(saved: EnrichmentListJourney): void {
    this.searchTerm = saved.searchTerm;
    this.appliedFilters = saved.filters;
    this.sort = saved.sort;
    this.config = {
      ...this.config,
      currentPage: saved.page,
      pageSize: saved.pageSize,
    };
    this.pickedScope = saved.pickedScope;
    this.codeDraft = saved.codeDraft;
    this.selectedChannel = saved.selectedChannel;
    this.enrichmentState.setChannel(saved.selectedChannel);
    this.priceStatusTab = saved.priceStatusTab;
    this.returnScroll = { top: saved.scrollTop, left: saved.scrollLeft };
  }

  /** Owner under the channel in scope now: the channel gate can change it
   *  after the row was built. */
  private ownerOf(row: EnrichmentPriceRow): PackageOwner {
    return packageOwner(row.productLine, this.selectedChannel);
  }

  private navigateToEdit(
    item: EnrichmentPriceRow,
    saved = this.captureJourney(),
  ): void {
    this.journey.save(saved);
    if (this.ownerOf(item) === "online-store") {
      const link = packageEditorLink(
        item.offeringId,
        item.priceId,
        "enrichment-price",
      );
      this.router.navigate(link.commands, { queryParams: link.queryParams });
      return;
    }
    this.router.navigate([
      APP_ROUTES.MANAGEMENT.ENRICHMENT_PRICE.EDIT_PACKAGE,
      item.offeringId,
      "price",
      item.priceId,
    ]);
  }

  /** Open the channel-picker modal that must be resolved before editing.
   *  Only reachable in the ApplicationOwner case with an empty pill —
   *  channel-scoped roles already have the nav-header channel in the store. */
  private openChannelGate(item: EnrichmentPriceRow): void {
    this.pendingEditItem = item;
    this.channelGateModalRef = this.omniarkModal.showTemplateModalWithRef(
      this.channelGateModalTemplate,
      {
        title: "",
        width: "440px",
        backdropDismiss: true,
        showCloseButton: false,
      },
    );
    this.channelGateModalRef.result.subscribe(() => {
      this.channelGateModalRef = null;
      this.pendingEditItem = null;
      this.pendingEditAllowedKeys = null;
      this.pendingEditChannelsLoading = false;
    });
    this.loadEditableChannels(item);
  }

  /** Ask the backend which directory channels {@link item} can be edited
   *  under, so the gate offers only channels whose scoped view lists it. A
   *  response for a row the gate no longer shows is dropped; a failure keeps
   *  the local salesChannels rule (allowedKeys stays null). */
  private loadEditableChannels(item: EnrichmentPriceRow): void {
    this.pendingEditAllowedKeys = null;
    this.pendingEditChannelsLoading = true;
    this.channelDirectory
      .load()
      .pipe(
        map((list) => list.map((c) => c.value).filter(Boolean)),
        switchMap((keys) =>
          keys.length
            ? this.enrichmentService.getEditableChannels(item.offeringId, keys)
            : of(null),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (resp) => {
          if (this.pendingEditItem !== item) return;
          this.pendingEditAllowedKeys = resp?.data?.channels ?? null;
          this.pendingEditChannelsLoading = false;
        },
        error: (error) => {
          if (this.pendingEditItem !== item) return;
          this.pendingEditChannelsLoading = false;
          logger.warn("Error loading editable channels:", error);
        },
      });
  }

  /** Close the channel gate without choosing (component "×" button). */
  closeChannelGate(): void {
    this.channelGateModalRef?.close();
    this.channelGateModalRef = null;
    this.pendingEditItem = null;
    this.pendingEditAllowedKeys = null;
    this.pendingEditChannelsLoading = false;
  }

  /** Channel chosen from the edit gate: persist it (drives `x-channel` for
   *  every API call, same as the in-page pill), close the modal, then
   *  continue to the edit page. */
  selectChannelForEdit(value: string): void {
    const saved = this.captureJourney();
    this.selectedChannel = value;
    this.enrichmentState.setChannel(value);
    const item = this.pendingEditItem;
    this.channelGateModalRef?.close();
    this.channelGateModalRef = null;
    this.pendingEditItem = null;
    this.pendingEditAllowedKeys = null;
    this.pendingEditChannelsLoading = false;
    if (item) this.navigateToEdit(item, saved);
  }

  /** Max pills shown inline in the CHANNELS cell before the rest
   *  collapse into a "+N" pill (tooltip carries the full list). Keeps long
   *  channel sets from stacking the row several pills tall. */
  private static readonly MAX_VISIBLE_PILLS = 3;

  visiblePills(list: readonly string[]): readonly string[] {
    return list.length <= EnrichmentList.MAX_VISIBLE_PILLS
      ? list
      : list.slice(0, EnrichmentList.MAX_VISIBLE_PILLS);
  }

  hiddenPillCount(list: readonly string[]): number {
    return Math.max(0, list.length - EnrichmentList.MAX_VISIBLE_PILLS);
  }

  copyPriceId(priceId: string): void {
    this.copyText(priceId, "Price ID copied");
  }

  /** The myAIS deep link for a price row — `""` (no button) when it cannot be
   *  built.
   *
   *  Two conditions, both of which hide the control rather than offer a link
   *  that fails on a phone:
   *
   *  - The link opens the **myAIS app**, so it is meaningless under any other
   *    channel scope. `isExactMyAisChannel` (not `isMyAisChannel`) because an
   *    unpicked scope is not myAIS — see channel.constants.
   *  - The app resolves the price by its catalog Product code alone, so a
   *    price the catalog has not coded gets no link; the POP id is not sent
   *    (see adl-deep-link.util).
   */
  appLinkFor(row: EnrichmentPriceRow): string {
    if (!isExactMyAisChannel(this.selectedChannel)) return "";
    return buildProductDetailAppLink(row.productCode);
  }

  copyAppLink(row: EnrichmentPriceRow): void {
    const link = this.appLinkFor(row);
    if (!link) return;
    this.copyText(link, "Deeplink copied");
  }

  /** Clipboard write with the textarea fallback — `navigator.clipboard` needs a
   *  secure context, which not every browser this page runs in provides. */
  private copyText(value: string, message: string): void {
    const done = (): void => {
      this.toast.showSuccess(message, { duration: 2000 });
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(value).then(done, () => undefined);
      return;
    }
    const ta = document.createElement("textarea");
    ta.value = value;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    ta.remove();
    done();
  }

  handlePreviewAction(item: EnrichmentPriceRow): void {
    // If a preview is already open, close it cleanly first so we don't end
    // up with two stacked drawers.
    this.closePreview();
    this.preview.open(item);
    const ref = this.drawerService.showDrawerRef(this.previewDrawerTemplate, {
      title: item.title,
      description: `${item.priceId} · in ${item.offeringName || item.offeringId}`,
      // Two columns (facts + a scaled phone) need ~880px; the list stays
      // visible beside it on any desktop viewport this app targets.
      width: "880px",
      position: "right",
      showCloseButton: true,
      backdropDismiss: true,
    });
    this.previewDrawerRef = ref;
    // The omniark drawer routes X / backdrop / Escape through `dismiss`, which
    // ERRORS the result stream rather than emitting — so a next-only
    // subscription (the assign drawer's pattern) never learns about them.
    // Listen on both terminal paths; a programmatic close() already went
    // through closePreview(), which nulls the ref first, so the guard keeps
    // this from tearing down a NEWER preview's state.
    const cleanup = (): void => {
      if (this.previewDrawerRef !== ref) return;
      this.previewDrawerRef = null;
      this.preview.close();
    };
    ref.result.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      complete: cleanup,
      error: cleanup,
    });
  }

  /** Single dispatcher for `<enr-table>` row actions. The shared component
   *  emits `{ actionId, item }` on `actionClick` — route to the matching
   *  legacy handler. */
  onTableAction(event: {
    actionId?: string;
    id?: string;
    item: EnrichmentPriceRow;
  }): void {
    const id = event.actionId ?? event.id;
    if (!id || !event.item) return;
    switch (id) {
      case "preview":
        this.handlePreviewAction(event.item);
        return;
      case "edit":
        this.handleEditAction(event.item);
        return;
    }
    const action = LIFECYCLE_ACTION_ORDER.find(
      (a) => LIFECYCLE_ACTIONS[a].menuId === id,
    );
    if (action) {
      this.openTransition(action, [event.item]);
      return;
    }
    const direction = SCHEDULE_DIRECTIONS.find(
      (d) => id === `cancel-schedule-${d}`,
    );
    if (direction) this.cancelOpenSchedule(event.item, direction);
  }

  /** The status cell's schedule chip: both directions, earliest first, e.g.
   *  "On 01/10/2026 09:00 · Off 31/10/2026 09:00". "" when none is open. */
  scheduleChipFor(row: EnrichmentPriceRow): string {
    const open = this.openSchedules.get(row.priceId);
    if (!open) return "";
    return [open.publish, open.unpublish]
      .filter((s): s is PublishSchedule => !!s)
      .sort((a, b) => Date.parse(a.scheduledAt) - Date.parse(b.scheduledAt))
      .map(
        (s) =>
          `${isTakeDown(s) ? "Off" : "On"} ${formatScheduledAt(s.scheduledAt)}`,
      )
      .join(" · ");
  }

  protected readonly scheduleZoneLabel = BANGKOK_ZONE_LABEL;

  private loadPageSchedules(): void {
    this.scheduleRequest?.unsubscribe();
    this.scheduleRequest = null;
    this.openSchedules = NO_OPEN_SCHEDULES;
    if (!this.isChannelSelected || !this.publishScheduleEnabled()) return;
    this.scheduleRequest = this.openScheduleLoader
      .load(this.tableData, this.selectedChannel)
      .subscribe((index) => (this.openSchedules = index));
  }

  /** Cancel one pending schedule of a row's price, after a confirm, then
   *  re-read the page's schedules. A batch cancel is not offered here: a
   *  batch covers prices this row does not show. */
  private cancelOpenSchedule(
    row: EnrichmentPriceRow,
    direction: PublishScheduleAction,
  ): void {
    const schedule = openScheduleOf(this.openSchedules, row.priceId, direction);
    if (!schedule || !isCancellableSchedule(schedule)) return;
    const channel = this.selectedChannel;
    const takeDown = direction === "unpublish";
    this.omniarkModal
      .showModal({
        title: takeDown
          ? "Cancel the scheduled stop selling?"
          : "Cancel the scheduled publish?",
        message: `${row.title} will not ${takeDown ? "come off sale" : "publish"} on ${formatScheduledAt(schedule.scheduledAt)} (${BANGKOK_ZONE_LABEL}). You can schedule it again afterwards.`,
        primaryButtonText: "Cancel schedule",
        secondaryButtonText: "Keep it",
        primaryButtonVariant: "error",
        secondaryButtonVariant: "text",
        showCloseButton: false,
        backdropDismiss: true,
      })
      .pipe(
        filter((choice: string | undefined) => choice === "primary"),
        switchMap(() =>
          this.enrichmentService
            .cancelPublishSchedule(schedule.scheduleId, {
              ...ENRICHMENT_HEADER_TRANSPORT,
              channel,
            })
            .pipe(
              map(() => true),
              catchError((error: unknown) => {
                const status = (error as { status?: number })?.status;
                // 400: the job already claimed it; 404: someone cancelled it
                // first. Either way the chips on screen are stale.
                this.toast.showError(
                  status === 400 || status === 404
                    ? "That schedule is no longer pending. The list has been refreshed."
                    : "Could not cancel the schedule. Please try again.",
                  { duration: 4000 },
                );
                return of(false);
              }),
            ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((cancelled) => {
        if (cancelled) {
          this.toast.showSuccess(
            takeDown
              ? "Scheduled stop selling cancelled"
              : "Scheduled publish cancelled",
            { duration: 3000 },
          );
        }
        this.loadPageSchedules();
      });
  }

  /** Run the offered term in the Product code scope, where it is an exact
   *  product code or number match. */
  searchAsProductCode(): void {
    const term = this.codeSearchOffer;
    if (!term) return;
    this.pickedScope = "productRefs";
    this.codeDraft = term;
    this.commitCodeSearch();
  }

  /** The Package cell: narrow the list to that package's prices through the
   *  Offering ID scope, the same filter the scope picker commits. */
  narrowToPackage(row: EnrichmentPriceRow): void {
    this.clearCommittedCodes();
    this.pickedScope = "offeringCode";
    this.codeDraft = row.offeringId;
    this.commitCodeSearch();
  }

  closePreview(): void {
    // Null the ref BEFORE closing so the drawer's own terminal callback (see
    // handlePreviewAction) sees a stale ref and leaves the controller alone —
    // the state is torn down exactly once, here.
    const ref = this.previewDrawerRef;
    this.previewDrawerRef = null;
    ref?.close();
    this.preview.close();
  }
}
